// src/pages/MallAdminPanel.js — FIXED Settings tab
// Fix 1: Save settings actually calls API and saves
// Fix 2: Commission type selector (% or flat AED)
// Fix 3: All fields controlled with state, saved together

import React, { useState, useEffect, useRef } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import {
  getMall, getMallAnalytics, getMallSettlement,
  addVendorToMall, removeVendorFromMall,
} from '../services/session';
import api from '../services/api';
import MallTablesTab from '../components/MallTablesTab';
import { getMallVendorPOS, saveMallVendorPOS, toggleMallVendorPOSPush } from '../services/pos';
import { useAuth } from '../context/AuthContext';
import MallReportsTab from '../components/MallReportsTab';
import MallOperationsTab from '../components/MallOperationsTab';
import ListFilters from '../components/ListFilters';
import Pagination from '../components/Pagination';

const ALL_PAYMENT_METHODS = [
  { id:'CASH',   icon:'💵', label:'Cash',        desc:'Manual cash collection'   },
  { id:'CARD',   icon:'💳', label:'Card',        desc:'Visa / Mastercard / Amex' },
  { id:'UPI',    icon:'📱', label:'UPI / QR',    desc:'Scan & pay via phone'     },
  { id:'WALLET', icon:'⚡', label:'Wallet',      desc:'Apple Pay / Google Pay'   },
  { id:'TAP',    icon:'🔵', label:'Tap Payments',desc:'UAE fast payment gateway' },
];

// ── Mall Payment Health Tab ───────────────────────────────────
function MallPaymentHealth({ stats, onRefresh }) {
  if (!stats) return (
    <div className="card" style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>
      <div style={{ fontSize:32, marginBottom:12 }}>💳</div>
      <div style={{ marginBottom:16 }}>No payment data loaded</div>
      <button className="btn btn-primary" onClick={onRefresh}>Load Payment Health</button>
    </div>
  );

  const srColor = stats.successRate >= 95 ? 'var(--green)' : stats.successRate >= 85 ? 'var(--accent)' : 'var(--red)';
  const vendors  = (stats.byVendor || []).slice().sort((a, b) => b.totalRevenue - a.totalRevenue);
  const stalls   = Object.entries(stats.byStall || {});

  return (
    <div>
      {/* Top KPIs */}
      <div className="grid4" style={{ marginBottom:20 }}>
        {[
          ['Success Rate',    `${stats.successRate}%`,              srColor],
          ['Total Attempts',  stats.totalAttempts,                  'var(--text)'],
          ['Total Revenue',   `AED ${Number(stats.totalRevenue||0).toFixed(0)}`, 'var(--green)'],
          ['Abandoned',       `${stats.totalAbandoned} orders`,     stats.totalAbandoned > 0 ? 'var(--accent)' : 'var(--text2)'],
        ].map(([l, v, c]) => (
          <div key={l} className="stat-card">
            <div className="stat-label">{l}</div>
            <div className="stat-val" style={{ color: c, fontSize:20 }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="grid2" style={{ marginBottom:20 }}>
        {/* Needs Attention */}
        <div className="card">
          <div className="section-label" style={{ marginBottom:12 }}>⚠️ Needs Attention</div>
          {stats.lowestSuccessRate ? (
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'#3d1a1a', borderRadius:8, border:'1px solid var(--red)' }}>
              <span style={{ fontSize:26 }}>{stats.lowestSuccessRate.emoji || '🍽'}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700 }}>{stats.lowestSuccessRate.name}</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>{stats.lowestSuccessRate.totalAttempts} attempts</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontWeight:700, color:'var(--red)', fontSize:18 }}>{stats.lowestSuccessRate.successRate}%</div>
                <div style={{ fontSize:11, color:'var(--text3)' }}>success rate</div>
              </div>
            </div>
          ) : (
            <div style={{ color:'var(--green)', fontSize:13 }}>✅ All vendors above threshold</div>
          )}
        </div>

        {/* Top failure reasons */}
        <div className="card">
          <div className="section-label" style={{ marginBottom:12 }}>❌ Top Failure Reasons</div>
          {(stats.failureReasons || []).length === 0
            ? <div style={{ color:'var(--text3)', fontSize:13 }}>No failures recorded</div>
            : (stats.failureReasons || []).map(({ reason, count }) => (
              <div key={reason} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontSize:13, color:'var(--text2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'75%' }}>{reason}</div>
                <span className="badge badge-red">{count}</span>
              </div>
            ))
          }
        </div>
      </div>

      {/* Per-vendor table */}
      <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:20 }}>
        <div style={{ padding:'13px 16px', borderBottom:'1px solid var(--border)', fontWeight:700 }}>
          Per-Stall Breakdown ({vendors.length} vendors)
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr>
              {['Vendor','Attempts','Success Rate','Revenue','Abandoned %','Failed'].map(h => (
                <th key={h} style={{ textAlign:'left', padding:'10px 14px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vendors.map(v => {
              const color = v.successRate >= 95 ? 'var(--green)' : v.successRate >= 85 ? 'var(--accent)' : 'var(--red)';
              return (
                <tr key={v.vendorId}>
                  <td style={{ padding:'11px 14px', fontWeight:600 }}>{v.emoji} {v.name}</td>
                  <td style={{ padding:'11px 14px', color:'var(--text2)' }}>{v.totalAttempts}</td>
                  <td style={{ padding:'11px 14px', fontWeight:700, color }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ flex:1, background:'var(--bg3)', borderRadius:4, height:6, minWidth:60 }}>
                        <div style={{ width:`${v.successRate}%`, background:color, height:6, borderRadius:4 }} />
                      </div>
                      {v.successRate}%
                    </div>
                  </td>
                  <td style={{ padding:'11px 14px', color:'var(--green)' }}>AED {Number(v.totalRevenue||0).toFixed(0)}</td>
                  <td style={{ padding:'11px 14px', color: v.abandonedRate > 10 ? 'var(--red)' : 'var(--text2)' }}>{v.abandonedRate}%</td>
                  <td style={{ padding:'11px 14px', color:'var(--red)' }}>{v.totalFailed}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Broken terminal detection */}
      {stalls.length > 0 && (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ padding:'13px 16px', borderBottom:'1px solid var(--border)', fontWeight:700 }}>
            🖥️ Terminal Health (by Kiosk/Source)
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr>
                {['Kiosk ID','Total','Success','Failed','Success Rate'].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'10px 14px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stalls.sort((a,b) => a[1].successRate - b[1].successRate).map(([kiosk, s]) => {
                const color = s.successRate >= 95 ? 'var(--green)' : s.successRate >= 80 ? 'var(--accent)' : 'var(--red)';
                return (
                  <tr key={kiosk}>
                    <td style={{ padding:'11px 14px', fontFamily:'monospace', fontSize:12 }}>{kiosk}</td>
                    <td style={{ padding:'11px 14px', color:'var(--text2)' }}>{s.total}</td>
                    <td style={{ padding:'11px 14px', color:'var(--green)' }}>{s.success}</td>
                    <td style={{ padding:'11px 14px', color:'var(--red)' }}>{s.failed}</td>
                    <td style={{ padding:'11px 14px', fontWeight:700, color }}>{s.successRate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Payouts Tab ───────────────────────────────────────────────
// pendingPayouts: per-vendor per-day format from getPendingPayouts
// completedPayouts: real PayoutRecord rows from getAllPayouts?status=PAID_BY_MALL
function PayoutsTab({ pendingPayouts, completedPayouts, onSettleDay, onSettleAll, heldDays, onToggleHold,
  completedDates, setCompletedDates, completedPage, setCompletedPage,
  completedVendorFilter, setCompletedVendorFilter, completedPagination, loadPayouts, vendors }) {
  const grandTotal    = pendingPayouts.reduce((s, v) => s + v.totalPending, 0);
  const vendorCount   = pendingPayouts.length;

  if (!pendingPayouts.length && !completedPayouts.length) return (
    <div className="card" style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>
      No payout data
    </div>
  );

  return (
    <div>
      <div className="grid3" style={{ marginBottom:20 }}>
        {[
          ['Vendors Owed',      vendorCount],
          ['Total Pending',     `AED ${grandTotal.toFixed(2)}`],
          ['Completed Payouts', completedPayouts.length],
        ].map(([l, v]) => (
          <div key={l} className="stat-card">
            <div className="stat-label">{l}</div>
            <div className="stat-val" style={{ fontSize:16 }}>{v}</div>
          </div>
        ))}
      </div>

      {/* ── Pending per vendor ── */}
      {pendingPayouts.length > 0 && (
        <>
          <div style={{ fontWeight:700, fontSize:15, marginBottom:12 }}>Pending Payouts</div>
          {pendingPayouts.map(vendor => {
            const vendorHeld  = heldDays[vendor.vendorId] || new Set();
            const activeDays  = vendor.days.filter(d => !vendorHeld.has(d.date));
            const activeTotal = parseFloat(activeDays.reduce((s, d) => s + d.net, 0).toFixed(2));
            return (
            <div key={vendor.vendorId} className="card" style={{ marginBottom:16, padding:0, overflow:'hidden' }}>
              {/* Vendor header */}
              <div style={{ padding:'13px 16px', background:'var(--bg3)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:22 }}>{vendor.emoji}</span>
                  <div>
                    <div style={{ fontWeight:700 }}>{vendor.vendorName}</div>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>
                      {vendor.commissionRate}% commission · {vendor.days.length} day{vendor.days.length !== 1 ? 's' : ''} pending
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontWeight:700, color:'var(--accent)', fontSize:15 }}>AED {vendor.totalPending.toFixed(2)}</div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>total pending</div>
                  </div>
                  {activeDays.length > 0 && (
                    <button className="btn btn-primary btn-sm" onClick={() => onSettleAll(vendor, activeDays)}>
                      💰 Settle All — AED {activeTotal.toFixed(2)}
                    </button>
                  )}
                </div>
              </div>
              {/* Per-day table */}
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr>
                    {['Date','Orders','Gross','Commission','Net Payable',''].map(h => (
                      <th key={h} style={{ textAlign:'left', padding:'8px 14px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vendor.days.map(day => {
                    const isHeld = vendorHeld.has(day.date);
                    return (
                    <tr key={day.date} style={{ borderBottom:'1px solid var(--border)', opacity:isHeld?0.45:1 }}>
                      <td style={{ padding:'10px 14px', fontWeight:600, textDecoration:isHeld?'line-through':'none' }}>{day.date}</td>
                      <td style={{ padding:'10px 14px', color:'var(--text2)' }}>{day.orders}</td>
                      <td style={{ padding:'10px 14px' }}>AED {day.gross.toFixed(2)}</td>
                      <td style={{ padding:'10px 14px', color:'var(--red)' }}>-AED {day.commission.toFixed(2)}</td>
                      <td style={{ padding:'10px 14px', fontWeight:700, color:'var(--green)' }}>AED {day.net.toFixed(2)}</td>
                      <td style={{ padding:'10px 14px', display:'flex', gap:6 }}>
                        {isHeld ? (
                          <button className="btn btn-ghost btn-sm" onClick={() => onToggleHold(vendor.vendorId, day.date)}>Unhold</button>
                        ) : (
                          <>
                            <button className="btn btn-ghost btn-sm" onClick={() => onSettleDay(vendor, day)}>Pay</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => onToggleHold(vendor.vendorId, day.date)}>Hold</button>
                          </>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })}
        </>
      )}

      {/* ── Completed payouts ── */}
      <div style={{ fontWeight:700, fontSize:15, marginBottom:12, marginTop: pendingPayouts.length ? 24 : 0 }}>Completed Payouts</div>
      <ListFilters
        startDate={completedDates.startDate}
        endDate={completedDates.endDate}
        maxRangeDays={30}
        onDateChange={({ startDate, endDate }) => {
          const nd = { startDate, endDate };
          setCompletedDates(nd);
          setCompletedPage(1);
          loadPayouts(1, nd, completedVendorFilter);
        }}
        filters={[
          {
            key: 'vendorId', label: 'Vendor', value: completedVendorFilter,
            options: ['', ...vendors.map(v => v.vendorId || v.id)].filter((v,i,a)=>a.indexOf(v)===i),
            onChange: v => {
              setCompletedVendorFilter(v);
              setCompletedPage(1);
              loadPayouts(1, completedDates, v);
            },
          },
        ]}
      />
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr>
              {['Vendor','Period','Amount','Reference','Paid On'].map(h => (
                <th key={h} style={{ textAlign:'left', padding:'8px 14px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {completedPayouts.length === 0 && (
              <tr><td colSpan={5} style={{ padding:32, textAlign:'center', color:'var(--text3)' }}>No records found for this period</td></tr>
            )}
            {completedPayouts.map(p => (
              <tr key={p.id} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'10px 14px', fontWeight:600 }}>{p.vendor?.emoji || '🍽'} {p.vendor?.name || p.vendorId}</td>
                <td style={{ padding:'10px 14px', color:'var(--text2)', fontSize:12 }}>
                  {p.periodStart ? `${new Date(p.periodStart).toLocaleDateString('en-GB',{day:'2-digit',month:'short'})} – ${new Date(p.periodEnd).toLocaleDateString('en-GB',{day:'2-digit',month:'short'})}` : '—'}
                </td>
                <td style={{ padding:'10px 14px', fontWeight:700, color:'var(--green)' }}>AED {Number(p.netAmount||0).toFixed(2)}</td>
                <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:12, color:'var(--text2)' }}>{p.transferRef || '—'}</td>
                <td style={{ padding:'10px 14px', color:'var(--text2)', fontSize:12 }}>
                  {p.paidAt ? new Date(p.paidAt).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={completedPagination?.page || completedPage}
        totalPages={completedPagination?.totalPages || 0}
        total={completedPagination?.total || 0}
        limit={completedPagination?.limit || 50}
        onPageChange={p => { setCompletedPage(p); loadPayouts(p, completedDates, completedVendorFilter); }}
      />
    </div>
  );
}

// ── POS Credentials Modal ─────────────────────────────────────
const POS_LABELS = {
  FOODICS:         { label: 'Foodics',         icon: '🍔' },
  SQUARE:          { label: 'Square POS',       icon: '⬛' },
  TOAST:           { label: 'Toast POS',        icon: '🍞' },
  LIGHTSPEED:      { label: 'Lightspeed',       icon: '⚡' },
  REVEL:           { label: 'Revel Systems',    icon: '🔄' },
  CLOVER:          { label: 'Clover',           icon: '🍀' },
  PETPOOJA:        { label: 'Pet Pooja',         icon: '🍛' },
  GENERIC_WEBHOOK: { label: 'Custom/Webhook',   icon: '🔗' },
};
const POS_TYPES = Object.keys(POS_LABELS);

function POSCredentialModal({ mallId, vendor, onClose, onSaved }) {
  const [form, setForm] = useState({
    posType: 'FOODICS', syncMode: 'WEBHOOK',
    apiKey: '', apiSecret: '', accessToken: '', apiEndpoint: '', webhookSecret: '', locationId: '',
    pushOrdersEnabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState('');

  useEffect(() => {
    getMallVendorPOS(mallId, vendor.id)
      .then((r) => {
        if (r.data?.connected && r.data.connection) {
          const c = r.data.connection;
          setForm((p) => ({
            ...p,
            posType:           c.posType           || 'FOODICS',
            syncMode:          c.syncMode           || 'WEBHOOK',
            apiEndpoint:       c.apiEndpoint        || '',
            locationId:        c.locationId         || '',
            accessToken:       c.accessToken        || '',
            pushOrdersEnabled: c.pushOrdersEnabled  !== false,
          }));
        }
      })
      .catch(() => {});
  }, [mallId, vendor.id]);

  const f = (key) => ({
    value:    form[key],
    onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })),
  });

  const handleSave = async () => {
    if (!form.apiKey && !form.apiEndpoint) {
      setMsg('❌ API Key is required');
      return;
    }
    setSaving(true); setMsg('');
    try {
      await saveMallVendorPOS(mallId, vendor.id, form);
      setMsg('✅ Saved');
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (err) {
      setMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
      setSaving(false);
    }
  };

  const posInfo = POS_LABELS[form.posType] || { label: form.posType, icon: '🔧' };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', padding: 28, width: 480, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>🔌 POS Credentials</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
              {vendor.emoji || '🍽'} {vendor.name}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {msg && (
          <div style={{
            padding: '9px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: msg.startsWith('✅') ? '#1a3d2a' : '#3d1a1a',
            color:      msg.startsWith('✅') ? 'var(--green)' : 'var(--red)',
          }}>{msg}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>POS System</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {POS_TYPES.map((type) => {
                const info = POS_LABELS[type];
                return (
                  <div key={type}
                    onClick={() => setForm((p) => ({ ...p, posType: type }))}
                    style={{
                      padding: '10px 8px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${form.posType === type ? 'var(--accent)' : 'var(--border)'}`,
                      background: form.posType === type ? '#3d2e10' : 'var(--bg3)',
                      fontSize: 11, fontWeight: 600,
                      color: form.posType === type ? 'var(--accent)' : 'var(--text2)',
                    }}>
                    <div style={{ fontSize: 18, marginBottom: 3 }}>{info.icon}</div>
                    {info.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>API Key *</div>
            <input type="password" placeholder={`${posInfo.label} API key`} {...f('apiKey')} />
          </div>

          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>API Secret</div>
            <input type="password" placeholder="API secret (if required)" {...f('apiSecret')} />
          </div>

          {form.posType === 'PETPOOJA' && (
            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Access Token *</div>
              <input type="password" placeholder="Pet Pooja access_token" {...f('accessToken')} />
            </div>
          )}

          {form.posType === 'GENERIC_WEBHOOK' && (
            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>POS Endpoint URL</div>
              <input type="text" placeholder="https://your-pos.com/api" {...f('apiEndpoint')} />
            </div>
          )}

          {['SQUARE', 'TOAST', 'LIGHTSPEED', 'PETPOOJA'].includes(form.posType) && (
            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Location ID</div>
              <input type="text" placeholder={form.posType === 'PETPOOJA' ? 'Pet Pooja restID' : 'Branch / location ID'} {...f('locationId')} />
            </div>
          )}

          {form.posType === 'REVEL' && (
            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Subdomain</div>
              <input type="text" placeholder="yourcompany (for yourcompany.revelup.com)" {...f('apiEndpoint')} />
            </div>
          )}

          {form.posType === 'CLOVER' && (
            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Merchant ID</div>
              <input type="text" placeholder="Clover merchant ID" {...f('locationId')} />
            </div>
          )}

          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>Sync Mode</div>
            <select {...f('syncMode')}>
              <option value="WEBHOOK">Webhook — real-time push from POS</option>
              <option value="API_PULL">API Pull — fetch on schedule</option>
            </select>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', background: 'var(--bg3)', borderRadius: 8,
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Push orders to POS</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                Auto-send paid orders to this vendor's kitchen system
              </div>
            </div>
            <div
              onClick={() => setForm((p) => ({ ...p, pushOrdersEnabled: !p.pushOrdersEnabled }))}
              style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative',
                background: form.pushOrdersEnabled ? 'var(--accent)' : 'var(--bg4)',
                transition: 'background .2s',
              }}>
              <div style={{
                position: 'absolute', top: 3, left: form.pushOrdersEnabled ? 22 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left .2s',
              }} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 2 }} onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Saving...' : '💾 Save Credentials'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vendor POS Tab ────────────────────────────────────────────
function VendorPOSTab({ mallId, vendors }) {
  const [posMap,    setPosMap]    = useState({});  // vendorId → { connected, connection }
  const [modal,     setModal]     = useState(null); // vendor object or null
  const [toggling,  setToggling]  = useState(null); // vendorId being toggled
  const [msg,       setMsg]       = useState('');

  // Fetch the server-side checklist for each vendor awaiting approval
 

  const showMsg = (text) => { setMsg(text); setTimeout(() => setMsg(''), 3000); };

  const loadAll = async () => {
    const map = {};
    await Promise.allSettled(
      vendors
        .filter((mv) => mv.vendor?.id)
        .map(async (mv) => {
          const vid = mv.vendor.id;
          try {
            const r = await getMallVendorPOS(mallId, vid);
            map[vid] = r.data;
          } catch (_) {
            // API error (e.g. vendor not in DB for this mall) → treat as not connected
            map[vid] = { connected: false, connection: null };
          }
        })
    );
    setPosMap(map);
  };

  useEffect(() => { if (vendors.length) loadAll(); }, [vendors, mallId]);

  const handleToggle = async (vendorId) => {
    setToggling(vendorId);
    try {
      const r = await toggleMallVendorPOSPush(mallId, vendorId);
      setPosMap((p) => ({
        ...p,
        [vendorId]: {
          ...p[vendorId],
          connection: { ...p[vendorId]?.connection, pushOrdersEnabled: r.data.pushOrdersEnabled },
        },
      }));
      showMsg(r.data.pushOrdersEnabled ? '✅ Order push enabled' : '✅ Order push disabled');
    } catch (_) {
      showMsg('❌ Toggle failed');
    } finally {
      setToggling(null);
    }
  };

  return (
    <div>
      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.startsWith('✅') ? '#1a3d2a' : '#3d1a1a',
          color:      msg.startsWith('✅') ? 'var(--green)' : 'var(--red)',
        }}>{msg}</div>
      )}

      <div className="card" style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--bg3)', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>
          <strong style={{ color: 'var(--text)' }}>POS Credentials</strong> — Enter each vendor's POS API key so orders are automatically pushed to their kitchen system when payment is confirmed.
          Vendors can also manage this themselves via their own Admin → POS Integration tab.
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>
          Vendor POS Status ({vendors.length})
        </div>

        {vendors.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>No vendors in mall</div>
        )}

        {vendors.map((mv) => {
          const v   = mv.vendor || {};
          const pos = posMap[v.id];
          const connected  = pos?.connected;
          const conn       = pos?.connection;
          const pushOn     = conn?.pushOrdersEnabled !== false;
          const posInfo    = conn ? (POS_LABELS[conn.posType] || { label: conn.posType, icon: '🔧' }) : null;

          return (
            <div key={mv.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 16px', borderBottom: '1px solid var(--border)',
              opacity: mv.isActive === false ? .55 : 1,
            }}>
              {/* Vendor info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 26 }}>{v.emoji || '🍽'}</span>
                <div>
                  <div style={{ fontWeight: 600 }}>{v.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                    {mv.stallNumber ? `Stall ${mv.stallNumber} · ` : ''}{v.email}
                  </div>
                </div>
              </div>

              {/* POS status + controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {pos === undefined ? (
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>Loading…</span>
                ) : connected ? (
                  <>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>
                        {posInfo?.icon} {posInfo?.label}
                      </div>
                      <div style={{ fontSize: 11, color: conn?.lastSyncStatus === 'success' ? 'var(--green)' : 'var(--text3)', marginTop: 1 }}>
                        {conn?.lastSyncAt
                          ? `Last sync ${new Date(conn.lastSyncAt).toLocaleDateString('en',{day:'2-digit',month:'short'})}`
                          : 'Never synced'}
                      </div>
                    </div>

                    {/* Push orders toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>Push</span>
                      <div
                        onClick={() => !toggling && handleToggle(v.id)}
                        style={{
                          width: 38, height: 20, borderRadius: 10, cursor: toggling === v.id ? 'wait' : 'pointer',
                          position: 'relative',
                          background: pushOn ? 'var(--accent)' : 'var(--bg4)',
                          opacity: toggling === v.id ? .5 : 1,
                          transition: 'background .2s',
                        }}>
                        <div style={{
                          position: 'absolute', top: 2, left: pushOn ? 18 : 2,
                          width: 16, height: 16, borderRadius: '50%', background: '#fff',
                          transition: 'left .2s',
                        }} />
                      </div>
                    </div>

                    <button className="btn btn-ghost btn-sm" onClick={() => setModal(v)}>Edit</button>
                  </>
                ) : (
                  <>
                    <span className="badge badge-red" style={{ fontSize: 11 }}>No POS</span>
                    <button className="btn btn-primary btn-sm" onClick={() => setModal(v)}>+ Configure</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modal && (
        <POSCredentialModal
          mallId={mallId}
          vendor={modal}
          onClose={() => setModal(null)}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}

const THEME_PRESETS = [
  { id:'dark',  label:'Dark',  bg:'#0f0f0f',  accent:'#f5a623' },
  { id:'light', label:'Light', bg:'#F8F9FA',  accent:'#E8A33D' },
  { id:'brand', label:'Brand', bg:'#0A1628',  accent:null },
];
// Literal (non-CSS-var) colors for the scoped kiosk mock preview — must NOT touch document-level theme
const THEME_COLORS = {
  dark:  { bg:'#0f0f0f', bg2:'#1a1a1a', bg3:'#242424', text:'#f0ede8', text2:'#a09890', accent:'#f5a623' },
  light: { bg:'#F8F9FA', bg2:'#FFFFFF', bg3:'#F0F2F5', text:'#1F2D3D', text2:'#4A5568', accent:'#E8A33D' },
  brand: { bg:'#0A1628', bg2:'#0F1F38', bg3:'#13243F', text:'#FFFFFF', text2:'#8FA3BF', accent:'#00A886' },
};

// ── Service Fee Section (inside Settings) ─────────────────────
function ServiceFeeSection({ mallId, mall, onSaved }) {
  const initFee = () => {
    const cfg = mall?.serviceFeeConfig;
    if (!cfg) return { enabled: false, type: 'FIXED', fixedAmount: 0, percentage: 0, maxCap: 0, minOrderAmount: 0, label: 'Service Fee', platformSharePercent: 60, mallSharePercent: 40 };
    const parsed = typeof cfg === 'string' ? (() => { try { return JSON.parse(cfg); } catch(_) { return {}; } })() : cfg;
    return { enabled: false, type: 'FIXED', fixedAmount: 0, percentage: 0, maxCap: 0, minOrderAmount: 0, label: 'Service Fee', platformSharePercent: 60, mallSharePercent: 40, ...parsed };
  };
  const [feeConfig,     setFeeConfig]     = React.useState(initFee);
  const [feeSaving,     setFeeSaving]     = React.useState(false);
  const [feeMsg,        setFeeMsg]        = React.useState('');
  const [feePreviewAmt, setFeePreviewAmt] = React.useState(100);

  // Keep platform+mall shares summing to 100
  const handlePlatformShare = (val) => {
    const n = Math.min(100, Math.max(0, parseInt(val) || 0));
    setFeeConfig(p => ({ ...p, platformSharePercent: n, mallSharePercent: 100 - n }));
  };

  const saveFee = async () => {
    if ((feeConfig.platformSharePercent + feeConfig.mallSharePercent) !== 100) {
      setFeeMsg('❌ Platform + Mall shares must equal 100'); return;
    }
    setFeeSaving(true);
    try {
      await api.patch(`/mall/${mallId}`, { serviceFeeConfig: feeConfig });
      setFeeMsg('✅ Saved');
      onSaved?.();
    } catch (err) {
      setFeeMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally {
      setFeeSaving(false);
      setTimeout(() => setFeeMsg(''), 3000);
    }
  };

  // inline calculateServiceFee to avoid import complexity at module top
  const previewFee = (() => {
    const cfg = feeConfig;
    if (!cfg.enabled) return { amount: 0 };
    if (cfg.minOrderAmount && feePreviewAmt < cfg.minOrderAmount) return { amount: 0 };
    let fee = 0;
    if (cfg.type === 'FIXED') fee = cfg.fixedAmount || 0;
    else if (cfg.type === 'PERCENTAGE') fee = feePreviewAmt * ((cfg.percentage || 0) / 100);
    else fee = (cfg.fixedAmount || 0) + feePreviewAmt * ((cfg.percentage || 0) / 100);
    if (cfg.maxCap && cfg.maxCap > 0 && fee > cfg.maxCap) fee = cfg.maxCap;
    return { amount: Math.round(fee * 100) / 100 };
  })();

  return (
    <div className="card" style={{ marginBottom:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div style={{ fontWeight:700, fontSize:16 }}>💰 Food Court Service Fee</div>
        {feeMsg && <span style={{ fontSize:13, color: feeMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{feeMsg}</span>}
      </div>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14 }}>
        Applied to all orders through the multi-vendor kiosk and table QR. Individual vendor fee configs do not apply on mall surfaces.
      </div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div>
          <div style={{ fontWeight:600, fontSize:13 }}>Enable Service Fee</div>
          <div style={{ fontSize:12, color:'var(--text2)' }}>Charge a service fee on food court orders</div>
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
          <input type="checkbox" checked={!!feeConfig.enabled}
            onChange={e => setFeeConfig(p => ({ ...p, enabled: e.target.checked }))} />
          <span style={{ fontSize:13 }}>{feeConfig.enabled ? 'On' : 'Off'}</span>
        </label>
      </div>

      {feeConfig.enabled && (
        <>
          <div style={{ marginBottom:12 }}>
            <div className="section-label" style={{ marginBottom:6 }}>Fee Type</div>
            <select value={feeConfig.type} onChange={e => setFeeConfig(p => ({ ...p, type: e.target.value }))}
              style={{ padding:'8px 12px', borderRadius:8, background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text)', fontSize:13 }}>
              <option value="FIXED">Fixed Amount</option>
              <option value="PERCENTAGE">Percentage</option>
              <option value="HYBRID">Hybrid (Fixed + Percentage)</option>
            </select>
          </div>

          {(feeConfig.type === 'FIXED' || feeConfig.type === 'HYBRID') && (
            <div style={{ marginBottom:12 }}>
              <div className="section-label" style={{ marginBottom:6 }}>Fixed Amount (AED)</div>
              <input type="number" min="0" step="0.01" value={feeConfig.fixedAmount || ''}
                onChange={e => setFeeConfig(p => ({ ...p, fixedAmount: parseFloat(e.target.value) || 0 }))}
                style={{ maxWidth:140 }} />
            </div>
          )}
          {(feeConfig.type === 'PERCENTAGE' || feeConfig.type === 'HYBRID') && (
            <div style={{ marginBottom:12 }}>
              <div className="section-label" style={{ marginBottom:6 }}>Percentage (%)</div>
              <input type="number" min="0" max="50" step="0.1" value={feeConfig.percentage || ''}
                onChange={e => setFeeConfig(p => ({ ...p, percentage: parseFloat(e.target.value) || 0 }))}
                style={{ maxWidth:140 }} />
            </div>
          )}

          <div style={{ marginBottom:12 }}>
            <div className="section-label" style={{ marginBottom:6 }}>Maximum Fee Cap (AED)</div>
            <input type="number" min="0" step="0.01" value={feeConfig.maxCap || ''}
              onChange={e => setFeeConfig(p => ({ ...p, maxCap: parseFloat(e.target.value) || 0 }))}
              style={{ maxWidth:140 }} />
            <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>Set 0 for no cap.</div>
          </div>

          <div style={{ marginBottom:12 }}>
            <div className="section-label" style={{ marginBottom:6 }}>Minimum Order for Fee (AED)</div>
            <input type="number" min="0" step="0.01" value={feeConfig.minOrderAmount || ''}
              onChange={e => setFeeConfig(p => ({ ...p, minOrderAmount: parseFloat(e.target.value) || 0 }))}
              style={{ maxWidth:140 }} />
          </div>

          <div style={{ marginBottom:12 }}>
            <div className="section-label" style={{ marginBottom:6 }}>Customer-Facing Label</div>
            <input type="text" value={feeConfig.label || 'Service Fee'}
              onChange={e => setFeeConfig(p => ({ ...p, label: e.target.value }))}
              placeholder="Service Fee" style={{ maxWidth:260 }} />
          </div>

          {/* Fee Split */}
          <div style={{ marginBottom:16 }}>
            <div className="section-label" style={{ marginBottom:8 }}>Fee Revenue Split</div>
            <div style={{ display:'flex', gap:20, flexWrap:'wrap', alignItems:'flex-end' }}>
              <div>
                <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}>Platform (%)</div>
                <input type="number" min="0" max="100" value={feeConfig.platformSharePercent ?? 60}
                  onChange={e => handlePlatformShare(e.target.value)}
                  style={{ maxWidth:80 }} />
              </div>
              <div>
                <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}>Mall (%) — auto</div>
                <input type="number" readOnly value={feeConfig.mallSharePercent ?? 40}
                  style={{ maxWidth:80, opacity:.7 }} />
              </div>
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>
              Service fee revenue is split between QuickServe and the mall per this configuration. Must sum to 100%.
            </div>
          </div>

          {/* Preview calculator */}
          <div style={{ padding:'14px 16px', background:'var(--bg3)', borderRadius:10, border:'1px solid var(--border)', marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Preview Calculator</div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <span style={{ fontSize:13, color:'var(--text2)' }}>On combined cart of AED</span>
              <input type="number" min="0" value={feePreviewAmt}
                onChange={e => setFeePreviewAmt(parseFloat(e.target.value) || 0)}
                style={{ maxWidth:80, padding:'4px 8px' }} />
            </div>
            <div style={{ fontSize:14, color: previewFee.amount > 0 ? 'var(--accent)' : 'var(--text3)' }}>
              Customer pays: AED {previewFee.amount.toFixed(2)} {previewFee.amount === 0 ? '(no fee)' : `${feeConfig.label || 'Service Fee'} → Platform: AED ${(previewFee.amount * (feeConfig.platformSharePercent ?? 60) / 100).toFixed(2)} | Mall: AED ${(previewFee.amount * (feeConfig.mallSharePercent ?? 40) / 100).toFixed(2)}`}
            </div>
          </div>
        </>
      )}

      <button className="btn btn-primary" disabled={feeSaving} onClick={saveFee}>
        {feeSaving ? '⏳ Saving...' : 'Save Service Fee'}
      </button>
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────
function SettingsTab({ mall, mallId, onSaved, vendors }) {
  const [form,    setForm]    = useState({
    name:            mall?.name            || '',
    billingMode:     mall?.billingMode     || 'COMBINED',
    commissionPct:   mall?.commissionPct   ?? 5,
    commissionType:  mall?.settings ? (
        typeof mall.settings === 'string'
          ? (JSON.parse(mall.settings).commissionType || 'PERCENT')
          : (mall.settings.commissionType || 'PERCENT')
      ) : 'PERCENT',
    taxRate:         mall?.taxRate         ?? 5,
    idleTimeoutSecs: mall?.idleTimeoutSecs ?? 60,
    currency:        mall?.currency        || 'AED',
  });
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState('');

  // QR ordering kill switch
  const [qrActive,       setQrActive]       = useState(mall?.isActive !== false);
  const [qrActiveSaving, setQrActiveSaving] = useState(false);
  useEffect(() => { setQrActive(mall?.isActive !== false); }, [mall?.isActive]);

  const handleToggleQrActive = async () => {
    const next = !qrActive;
    setQrActiveSaving(true);
    setMsg('');
    try {
      await api.patch('/mall/settings/active', { isActive: next });
      setQrActive(next);
      setMsg(next ? '✅ QR ordering is now ON' : '✅ QR ordering is now OFF — table QR codes are disabled');
      onSaved?.();
    } catch (err) {
      setMsg('❌ ' + (err.response?.data?.error || 'Failed to update QR ordering'));
    } finally {
      setQrActiveSaving(false);
      setTimeout(() => setMsg(''), 4000);
    }
  };

  // Kiosk appearance
  const [kioskTheme,  setKioskTheme]  = useState(mall?.kioskTheme || 'dark');
  const [brandColor,  setBrandColor]  = useState(mall?.brandColor || '#00A886');
  const [bgColor,     setBgColor]     = useState(mall?.bgColor || '#0A1628');
  const [tileColor,   setTileColor]   = useState(mall?.tileColor || '#0F1F38');
  const [fontColor,   setFontColor]   = useState(mall?.fontColor || '#FFFFFF');
  const [buttonTextColor, setButtonTextColor] = useState(mall?.buttonTextColor || '#000000');
  const [surfaceColor, setSurfaceColor] = useState(mall?.surfaceColor || '#13243F');
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeMsg,    setThemeMsg]    = useState('');

  // Payment methods (multi-vendor kiosk + table QR)
  const [payConfig, setPayConfig] = useState(
    mall?.paymentConfig || { CASH:true, CARD:true, UPI:true, WALLET:false, TAP:false, allowCashOnQR:false }
  );
  const [payMsg, setPayMsg] = useState('');

  // Payment collection mode — PLATFORM_DEFAULT treated as MALL_COLLECTOR in UI
  const normaliseMode = (m) => (!m || m === 'PLATFORM_DEFAULT') ? 'MALL_COLLECTOR' : m;
  const [collectionMode,   setCollectionMode]   = useState(normaliseMode(mall?.collectionMode));
  const [collectionSaving, setCollectionSaving] = useState(false);
  const [collectionMsg,    setCollectionMsg]    = useState('');

  // Tap credential form (MALL_MARKETPLACE only) — keys are write-only after save
  const [tapCreds, setTapCreds] = useState({
    tapMarketplaceKey: '', tapMerchantKey: '',
    tapMarketplaceId:  mall?.tapMarketplaceId  || '',
    tapQuickServeRetailerId: mall?.tapQuickServeRetailerId || '',
  });
  const [tapCredSaving,  setTapCredSaving]  = useState(false);
  const [tapCredMsg,     setTapCredMsg]     = useState('');
  const [testingConn,    setTestingConn]    = useState(false);
  const [connStatus,     setConnStatus]     = useState(
    mall?.tapConnectionVerifiedAt
      ? { ok: true,  msg: `Last verified ${new Date(mall.tapConnectionVerifiedAt).toLocaleDateString()}` }
      : null
  );

  // Vendor onboarding modal
  const [onboardVendor,  setOnboardVendor]  = useState(null);   // vendor obj or null
  const [onboardStep,    setOnboardStep]    = useState(1);
  const [onboardForm,    setOnboardForm]    = useState({});
  const [onboardFiles,   setOnboardFiles]   = useState({});      // { commercialReg, tradeLicense, ownerId, ibanCert }
  const [uploadStatus,   setUploadStatus]   = useState({});      // { commercialReg: 'uploading'|'done'|'error', ... }
  const [fileIds,        setFileIds]        = useState({});       // { commercialReg: 'file_xxx', ... }
  const [onboardSaving,  setOnboardSaving]  = useState(false);
  const [onboardMsg,     setOnboardMsg]     = useState('');

  const saveCollectionMode = async () => {
    setCollectionSaving(true);
    // PLATFORM_DEFAULT is no longer a UI choice; saving always uses MALL_COLLECTOR or MALL_MARKETPLACE
    try {
      await api.patch(`/mall/${mallId}`, { collectionMode });
      setCollectionMsg('✅ Saved');
      onSaved?.();
    } catch (err) {
      setCollectionMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally {
      setCollectionSaving(false);
      setTimeout(() => setCollectionMsg(''), 3000);
    }
  };

  const saveTapCredentials = async () => {
    // Only send fields that the admin has actually typed into
    const payload = {};
    if (tapCreds.tapMarketplaceKey.trim())       payload.tapMarketplaceKey       = tapCreds.tapMarketplaceKey.trim();
    if (tapCreds.tapMerchantKey.trim())          payload.tapMerchantKey          = tapCreds.tapMerchantKey.trim();
    if (tapCreds.tapMarketplaceId.trim())        payload.tapMarketplaceId        = tapCreds.tapMarketplaceId.trim();
    if (tapCreds.tapQuickServeRetailerId.trim()) payload.tapQuickServeRetailerId = tapCreds.tapQuickServeRetailerId.trim();
    if (!Object.keys(payload).length) { setTapCredMsg('❌ Enter at least one field to update'); return; }
    setTapCredSaving(true);
    try {
      await api.patch(`/mall/${mallId}/tap/credentials`, payload);
      setTapCredMsg('✅ Credentials saved (encrypted)');
      // Clear secret key fields after save; keep non-secret fields
      setTapCreds(p => ({ ...p, tapMarketplaceKey: '', tapMerchantKey: '' }));
      setConnStatus(null); // reset connection status after new keys
      onSaved?.();
    } catch (err) {
      setTapCredMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally {
      setTapCredSaving(false);
      setTimeout(() => setTapCredMsg(''), 4000);
    }
  };

  const testTapConnection = async () => {
    setTestingConn(true);
    setConnStatus(null);
    try {
      const r = await api.post(`/mall/${mallId}/tap/test-connection`);
      setConnStatus({ ok: true, msg: `Connected — ${r.data.accountName || 'account verified'}` });
    } catch (err) {
      setConnStatus({ ok: false, msg: err.response?.data?.error || 'Connection failed' });
    } finally {
      setTestingConn(false);
    }
  };

  const openOnboarding = (mv) => {
    const v = mv.vendor || mv;
    setOnboardVendor(mv);
    setOnboardStep(1);
    setOnboardForm({ name: v.name || '', nameAr: '', email: v.email || '', phone: v.phone || '', businessType: 'corp' });
    setOnboardFiles({});
    setUploadStatus({});
    setFileIds({});
    setOnboardMsg('');
  };

  const uploadKycFile = async (field, file) => {
    if (!file) return;
    setUploadStatus(s => ({ ...s, [field]: 'uploading' }));
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('purpose', field === 'commercialReg' ? 'commercial_registration'
                           : field === 'tradeLicense'  ? 'trade_license' : 'id');
      form.append('title', file.name);
      const r = await api.post(`/mall/${mallId}/vendors/${(onboardVendor?.vendor || onboardVendor)?.id || onboardVendor?.vendorId}/tap/upload-kyc`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFileIds(f => ({ ...f, [field]: r.data.fileId }));
      setUploadStatus(s => ({ ...s, [field]: 'done' }));
    } catch (err) {
      setUploadStatus(s => ({ ...s, [field]: 'error' }));
    }
  };

  const submitOnboarding = async () => {
    setOnboardSaving(true);
    setOnboardMsg('');
    try {
      const vendorId = (onboardVendor?.vendor || onboardVendor)?.id || onboardVendor?.vendorId;
      await api.post(`/mall/${mallId}/vendors/${vendorId}/tap/onboard`, {
        ...onboardForm, fileIds,
      });
      setOnboardMsg('✅ Submitted to Tap — vendor status updated to Pending KYC');
      onSaved?.();
      setTimeout(() => { setOnboardVendor(null); setOnboardMsg(''); }, 2500);
    } catch (err) {
      setOnboardMsg('❌ ' + (err.response?.data?.error || 'Submission failed'));
    } finally {
      setOnboardSaving(false);
    }
  };

  const handleTogglePayment = async (method) => {
    const updated = { ...payConfig, [method]: !payConfig[method] };
    setPayConfig(updated);
    try {
      await api.patch(`/mall/${mallId}/payment-config`, updated);
      setPayMsg('✅ Saved');
      onSaved?.();
    } catch (_) { setPayMsg('❌ Save failed'); }
    setTimeout(() => setPayMsg(''), 2000);
  };

  const saveTheme = async () => {
    if (brandColor && !/^#[0-9A-Fa-f]{6}$/.test(brandColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (bgColor && !/^#[0-9A-Fa-f]{6}$/.test(bgColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (tileColor && !/^#[0-9A-Fa-f]{6}$/.test(tileColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (fontColor && !/^#[0-9A-Fa-f]{6}$/.test(fontColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (buttonTextColor && !/^#[0-9A-Fa-f]{6}$/.test(buttonTextColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    if (surfaceColor && !/^#[0-9A-Fa-f]{6}$/.test(surfaceColor)) {
      setThemeMsg('❌ Invalid hex color'); setTimeout(() => setThemeMsg(''), 3000); return;
    }
    setThemeSaving(true);
    try {
      await api.patch(`/mall/${mallId}`, { kioskTheme, brandColor, bgColor, tileColor, fontColor, buttonTextColor, surfaceColor });
      setThemeMsg('✅ Saved');
      onSaved?.();
    } catch (err) {
      setThemeMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally {
      setThemeSaving(false);
      setTimeout(() => setThemeMsg(''), 3000);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await api.patch(`/mall/${mallId}`, form);
      setMsg('✅ Settings saved successfully');
      onSaved?.();
    } catch (err) {
      setMsg('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const f = (key) => ({
    value:    form[key],
    onChange: (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  return (
    <div>
      {msg && (
        <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.startsWith('✅') ? '#1a3d2a' : '#3d1a1a',
          color:      msg.startsWith('✅') ? 'var(--green)' : 'var(--red)',
          border:     `1px solid ${msg.startsWith('✅') ? 'var(--green)' : 'var(--red)'}`,
        }}>{msg}</div>
      )}

      <div className="card" style={{ marginBottom: 16, border: `1px solid ${qrActive ? 'var(--green)' : 'var(--red)'}` }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>📱 QR Ordering Active</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>
              When OFF, all table QR codes stop working immediately. Customers will see an unavailability message.
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
            <span style={{ fontSize:12, fontWeight:600, color: qrActive ? 'var(--green)' : 'var(--red)' }}>
              {qrActive ? 'ON' : 'OFF'}
            </span>
            <div className={`toggle${qrActive ? ' on' : ''}`}
              onClick={qrActiveSaving ? undefined : handleToggleQrActive}
              style={{ cursor: qrActiveSaving ? 'wait' : 'pointer', opacity: qrActiveSaving ? 0.6 : 1,
                       background: qrActive ? 'var(--green)' : 'var(--red)' }} />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>🏬 Mall Details</div>
        <div className="grid2" style={{ gap: 16 }}>
          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>Mall Name</div>
            <input type="text" {...f('name')} placeholder="e.g. Dubai Mall Food Court" />
          </div>
          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>Currency</div>
            <select {...f('currency')}>
              <option value="AED">AED — UAE Dirham</option>
              <option value="USD">USD — US Dollar</option>
              <option value="SAR">SAR — Saudi Riyal</option>
              <option value="QAR">QAR — Qatari Riyal</option>
              <option value="KWD">KWD — Kuwaiti Dinar</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>💳 Payment Methods</div>
          {payMsg && <span style={{ fontSize:13, color: payMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{payMsg}</span>}
        </div>
        <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
          These payment methods apply to the multi-vendor kiosk and table QR ordering for this food court.
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {ALL_PAYMENT_METHODS.map((m) => (
            <div key={m.id} style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'14px 16px', background:'var(--bg3)',
              borderRadius:10, border:`1px solid ${payConfig[m.id] ? 'var(--green)' : 'var(--border)'}`,
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:28 }}>{m.icon}</span>
                <div>
                  <div style={{ fontWeight:600 }}>{m.label}</div>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{m.desc}</div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:12, color: payConfig[m.id] ? 'var(--green)' : 'var(--text3)' }}>
                  {payConfig[m.id] ? 'Enabled' : 'Disabled'}
                </span>
                <div className={`toggle${payConfig[m.id] ? ' on' : ''}`}
                  onClick={() => handleTogglePayment(m.id)}
                  style={{ cursor:'pointer' }} />
              </div>
            </div>
          ))}
        </div>

        {/* Cash on QR orders */}
        {payConfig.CASH && (
          <div style={{ marginTop:16, padding:'14px 16px', background:'var(--bg3)', borderRadius:10, border:`1px solid ${payConfig.allowCashOnQR ? 'var(--accent)' : 'var(--border)'}` }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:24 }}>📱</span>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>Allow Cash on QR Orders</div>
                  <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>
                    When OFF (recommended): customers ordering via QR must pay by card or digital payment only.
                  </div>
                  <div style={{ fontSize:11, color:'var(--red)', marginTop:2 }}>
                    When ON: customer can select cash but must acknowledge going to counter first.
                  </div>
                </div>
              </div>
              <div className={`toggle${payConfig.allowCashOnQR ? ' on' : ''}`}
                onClick={() => handleTogglePayment('allowCashOnQR')}
                style={{ cursor:'pointer', flexShrink:0 }} />
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>💳 Billing Configuration</div>
        <div className="grid2" style={{ gap: 16 }}>
          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>Billing Mode</div>
            <select {...f('billingMode')}>
              <option value="INDIVIDUAL">Individual — customer pays each vendor separately</option>
              <option value="COMBINED">Combined — customer pays once, mall settles vendors</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
              {form.billingMode === 'COMBINED'
                ? '💡 Customer pays once. Mall holds funds and settles vendors daily.'
                : '💡 Customer pays each stall directly. No mall settlement needed.'}
            </div>
          </div>
          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>VAT / Tax Rate (%)</div>
            <input type="number" {...f('taxRate')} min="0" max="100" step="0.5" placeholder="5" />
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>Standard UAE VAT is 5%</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>💰 Commission Settings</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          Commission is deducted from vendor payouts when using Combined billing mode.
        </div>
        <div className="grid2" style={{ gap: 16 }}>

          {/* Commission type — PERCENT or FLAT */}
          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>Commission Type</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['PERCENT', '% Percentage'], ['FLAT', 'Flat AED Amount']].map(([val, label]) => (
                <div key={val}
                  onClick={() => setForm((p) => ({ ...p, commissionType: val }))}
                  style={{
                    flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                    border: `2px solid ${form.commissionType === val ? 'var(--accent)' : 'var(--border)'}`,
                    background: form.commissionType === val ? '#3d2e10' : 'var(--bg3)',
                    color: form.commissionType === val ? 'var(--accent)' : 'var(--text2)',
                    fontSize: 13, fontWeight: 600,
                  }}>
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* Commission value */}
          <div>
            <div className="section-label" style={{ marginBottom: 6 }}>
              Commission {form.commissionType === 'PERCENT' ? 'Rate (%)' : 'Amount (AED)'}
            </div>
            <input type="number" {...f('commissionPct')} min="0"
              max={form.commissionType === 'PERCENT' ? 100 : undefined}
              step={form.commissionType === 'PERCENT' ? 0.5 : 1}
              placeholder={form.commissionType === 'PERCENT' ? '5' : '10'} />
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
              {form.commissionType === 'PERCENT'
                ? `Example: 5% of AED 100 sale = AED 5 commission, vendor gets AED 95`
                : `Example: AED ${form.commissionPct || 10} flat per transaction, regardless of sale amount`}
            </div>
          </div>
        </div>

        {/* Live commission preview */}
        {form.commissionPct > 0 && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--bg3)', borderRadius: 8, fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>📊 Commission Preview</div>
            {[50, 100, 200, 500].map((sale) => {
              const comm = form.commissionType === 'PERCENT'
                ? (sale * Number(form.commissionPct) / 100)
                : Number(form.commissionPct);
              return (
                <div key={sale} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text2)', marginBottom: 4 }}>
                  <span>AED {sale} sale</span>
                  <span>
                    Commission: <strong style={{ color: 'var(--red)' }}>AED {comm.toFixed(2)}</strong> →
                    Vendor gets: <strong style={{ color: 'var(--green)' }}>AED {(sale - comm).toFixed(2)}</strong>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>⏱ Kiosk Behaviour</div>
        <div>
          <div className="section-label" style={{ marginBottom: 6 }}>Idle Timeout (seconds)</div>
          <input type="number" {...f('idleTimeoutSecs')} min="30" max="300" step="10" style={{ maxWidth: 200 }} />
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
            Kiosk resets to attract screen after this many seconds of no activity.
            Current: <strong>{form.idleTimeoutSecs}s</strong> ({Math.round(form.idleTimeoutSecs / 60 * 10) / 10} min)
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>🎨 Food Court Kiosk Appearance</div>
          {themeMsg && <span style={{ fontSize:13, color:themeMsg.startsWith('✅')?'var(--green)':'var(--red)' }}>{themeMsg}</span>}
        </div>
        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
          {THEME_PRESETS.map(p => {
            const selected = kioskTheme === p.id;
            const swatchAccent = p.id === 'brand' ? (brandColor || '#00A886') : p.accent;
            return (
              <div key={p.id}
                onClick={() => setKioskTheme(p.id)}
                style={{
                  width:160, height:100, borderRadius:10, cursor:'pointer',
                  background: p.bg, border: selected ? '2px solid var(--accent)' : '2px solid var(--border)',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  gap:8, position:'relative',
                }}>
                {selected && <div style={{ position:'absolute', top:6, right:8, color:'var(--accent)', fontSize:14 }}>✓</div>}
                <div style={{ width:24, height:24, borderRadius:'50%', background:swatchAccent }} />
                <div style={{ fontSize:13, fontWeight:600, color: p.id === 'light' ? '#1F2D3D' : '#fff' }}>{p.label}</div>
              </div>
            );
          })}
        </div>

        {kioskTheme === 'brand' && (
          <div style={{ marginBottom:16 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>Brand color</div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input type="color" value={brandColor || '#00A886'}
                onChange={e => setBrandColor(e.target.value)}
                style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
              <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                value={brandColor || ''}
                onChange={e => setBrandColor(e.target.value)}
                placeholder="#00A886"
                style={{ maxWidth:140 }} />
            </div>
          </div>
        )}

        {kioskTheme === 'brand' && (
          <div style={{ marginBottom:16 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>Background color</div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input type="color" value={bgColor || '#0A1628'}
                onChange={e => setBgColor(e.target.value)}
                style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
              <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                value={bgColor || ''}
                onChange={e => setBgColor(e.target.value)}
                placeholder="#0A1628"
                style={{ maxWidth:140 }} />
            </div>
          </div>
        )}

        {kioskTheme === 'brand' && (
          <div style={{ marginBottom:16 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>Tiles color</div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input type="color" value={tileColor || '#0F1F38'}
                onChange={e => setTileColor(e.target.value)}
                style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
              <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                value={tileColor || ''}
                onChange={e => setTileColor(e.target.value)}
                placeholder="#0F1F38"
                style={{ maxWidth:140 }} />
            </div>
          </div>
        )}

        {kioskTheme === 'brand' && (
          <div style={{ marginBottom:16 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>Font color</div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input type="color" value={fontColor || '#FFFFFF'}
                onChange={e => setFontColor(e.target.value)}
                style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
              <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                value={fontColor || ''}
                onChange={e => setFontColor(e.target.value)}
                placeholder="#FFFFFF"
                style={{ maxWidth:140 }} />
            </div>
          </div>
        )}

        {kioskTheme === 'brand' && (
          <div style={{ marginBottom:16 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>Button text color</div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input type="color" value={buttonTextColor || '#000000'}
                onChange={e => setButtonTextColor(e.target.value)}
                style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
              <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                value={buttonTextColor || ''}
                onChange={e => setButtonTextColor(e.target.value)}
                placeholder="#000000"
                style={{ maxWidth:140 }} />
            </div>
          </div>
        )}

        {kioskTheme === 'brand' && (
          <div style={{ marginBottom:16 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>Surface color</div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <input type="color" value={surfaceColor || '#13243F'}
                onChange={e => setSurfaceColor(e.target.value)}
                style={{ width:48, height:38, padding:2, cursor:'pointer' }} />
              <input type="text" pattern="^#[0-9A-Fa-f]{6}$"
                value={surfaceColor || ''}
                onChange={e => setSurfaceColor(e.target.value)}
                placeholder="#13243F"
                style={{ maxWidth:140 }} />
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>
              This color is used for the search bar, language button, category tabs, and item image placeholders.
            </div>
          </div>
        )}

        {/* Live mock preview — uses literal colors, never touches the document theme */}
        {(() => {
          const c   = THEME_COLORS[kioskTheme] || THEME_COLORS.dark;
          const ac  = kioskTheme === 'brand' ? (brandColor || c.accent) : c.accent;
          const bg  = kioskTheme === 'brand' ? (bgColor || c.bg) : c.bg;
          const tbg = kioskTheme === 'brand' ? (tileColor || c.bg2) : c.bg2;
          const tc  = kioskTheme === 'brand' ? (fontColor || c.text) : c.text;
          const btc = kioskTheme === 'brand' ? (buttonTextColor || '#000') : '#000';
          const sbg = kioskTheme === 'brand' ? (surfaceColor || c.bg3) : c.bg3;
          return (
            <div style={{ marginBottom:16 }}>
              <div className="section-label" style={{ marginBottom:6 }}>Kiosk preview</div>
              <div style={{ background:bg, borderRadius:10, padding:14, border:'1px solid var(--border)' }}>
                <div style={{ fontSize:13, fontWeight:700, color:tc, marginBottom:10 }}>
                  {mall?.name || 'Food Court'}
                </div>
                <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                  <div style={{ flex:1, background:sbg, borderRadius:6, padding:'6px 10px', fontSize:11, color:tc }}>🔍 Search</div>
                  <div style={{ background:sbg, borderRadius:6, padding:'6px 10px', fontSize:11, color:tc }}>🌐 EN</div>
                  <div style={{ background:sbg, borderRadius:6, padding:'6px 10px', fontSize:11, color:tc }}>Category</div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  {[1,2].map(i => (
                    <div key={i} style={{ flex:1, background:tbg, borderRadius:8, padding:10 }}>
                      <div style={{ fontSize:11, color:tc, marginBottom:6 }}>Sample Item {i}</div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:13, fontWeight:700, color:ac }}>AED {i * 12}.00</span>
                        <span style={{ width:20, height:20, borderRadius:'50%', background:ac, color:btc, fontSize:12, display:'flex', alignItems:'center', justifyContent:'center' }}>+</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
          Applied to the shared multi-vendor kiosk and all table QR ordering pages for this food court.
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-primary" disabled={themeSaving} onClick={saveTheme}>
            {themeSaving ? '⏳ Saving...' : 'Save Appearance'}
          </button>
        </div>
      </div>

      {/* ── Payment Collection ── */}
      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:14 }}>💰 Payment Collection</div>

        {/* Two options only — PLATFORM_DEFAULT is treated as MALL_COLLECTOR */}
        <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
          {[
            {
              value: 'MALL_COLLECTOR',
              label: 'Manual Settlement',
              desc:  'You collect all payments. QuickServe tracks what you owe each vendor daily. Your finance team transfers to vendors and marks payouts as done.',
            },
            {
              value: 'MALL_MARKETPLACE',
              label: 'Tap Marketplace (Auto-Split)',
              desc:  'Payments automatically split to vendors at checkout via your Tap marketplace account. Only cash requires manual settlement.',
            },
          ].map(opt => (
            <label key={opt.value} style={{
              display:'flex', alignItems:'flex-start', gap:12, padding:'12px 14px',
              background: collectionMode === opt.value ? '#0f2d1f' : 'var(--bg3)',
              border: `1px solid ${collectionMode === opt.value ? 'var(--green)' : 'var(--border)'}`,
              borderRadius:8, cursor:'pointer',
            }}>
              <input type="radio" name="collectionMode" value={opt.value}
                checked={collectionMode === opt.value}
                onChange={() => setCollectionMode(opt.value)}
                style={{ marginTop:3 }} />
              <div>
                <div style={{ fontWeight:600, fontSize:13 }}>{opt.label}</div>
                <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>

        {collectionMsg && <div style={{ fontSize:13, marginBottom:10, color: collectionMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{collectionMsg}</div>}
        <button className="btn btn-primary" style={{ marginBottom: collectionMode === 'MALL_MARKETPLACE' ? 20 : 0 }}
          disabled={collectionSaving} onClick={saveCollectionMode}>
          {collectionSaving ? '⏳ Saving...' : 'Save'}
        </button>

        {/* ── Tap Marketplace configuration (shown when selected) ── */}
        {collectionMode === 'MALL_MARKETPLACE' && (
          <>
            {/* Step 1: Credentials */}
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginTop:4 }}>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>🏦 Step 1 — Enter Your Tap Credentials</div>
              <div style={{ fontSize:12, color:'var(--text3)', marginBottom:14 }}>
                These are your mall's Tap marketplace API keys. Get them from your Tap Dashboard → Developers → Keys.
                Keys are encrypted before storage and can never be read back.
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {[
                  { key:'tapMarketplaceKey', label:'Tap Marketplace Key', placeholder:'sk_live_marketplace_...', secret:true,
                    hint:'From Tap Dashboard → Developers → Marketplace Key' },
                  { key:'tapMerchantKey',    label:'Tap Merchant Key',    placeholder:'sk_live_...',             secret:true,
                    hint:'From Tap Dashboard → Developers → Secret Key' },
                  { key:'tapMarketplaceId',  label:'Tap Marketplace ID',  placeholder:'1234567',                 secret:false,
                    hint:'Your numeric marketplace account ID from Tap' },
                ].map(f => (
                  <div key={f.key}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                      <div className="section-label">{f.label}</div>
                      {f.secret && mall?.tapMarketplaceKey && f.key === 'tapMarketplaceKey' && (
                        <span style={{ fontSize:11, color:'var(--green)' }}>🔒 Configured</span>
                      )}
                      {f.secret && mall?.tapMerchantKey && f.key === 'tapMerchantKey' && (
                        <span style={{ fontSize:11, color:'var(--green)' }}>🔒 Configured</span>
                      )}
                    </div>
                    <input
                      type={f.secret ? 'password' : 'text'}
                      placeholder={f.secret && mall?.[f.key] ? '●●●●●●●● (set — enter new value to rotate)' : f.placeholder}
                      value={tapCreds[f.key]}
                      onChange={e => setTapCreds(p => ({ ...p, [f.key]: e.target.value }))}
                      autoComplete="new-password"
                    />
                    <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>{f.hint}</div>
                  </div>
                ))}

                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                    <div className="section-label">QuickServe Destination ID <span style={{ fontWeight:400, color:'var(--text3)' }}>(optional)</span></div>
                  </div>
                  <input type="text" placeholder="dest_qs_xxx"
                    value={tapCreds.tapQuickServeRetailerId}
                    onChange={e => setTapCreds(p => ({ ...p, tapQuickServeRetailerId: e.target.value }))} />
                  <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>
                    In your Tap Dashboard, create a destination for QuickServe UAE and paste the destination_id here.
                    If not set, QuickServe's fee is invoiced separately.
                  </div>
                </div>
              </div>

              {tapCredMsg && <div style={{ fontSize:13, marginTop:10, color: tapCredMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{tapCredMsg}</div>}
              <div style={{ display:'flex', gap:10, marginTop:12 }}>
                <button className="btn btn-primary btn-sm" disabled={tapCredSaving} onClick={saveTapCredentials}>
                  {tapCredSaving ? '⏳ Saving...' : '🔒 Save Credentials'}
                </button>
                <button className="btn btn-ghost btn-sm" disabled={testingConn || !mall?.tapMarketplaceKey} onClick={testTapConnection}>
                  {testingConn ? '⏳ Testing...' : '🔌 Test Connection'}
                </button>
              </div>
              {connStatus && (
                <div style={{ marginTop:10, fontSize:13, color: connStatus.ok ? 'var(--green)' : 'var(--red)' }}>
                  {connStatus.ok ? '● ' : '⚠ '}{connStatus.msg}
                </div>
              )}
            </div>

            {/* Step 2: QuickServe bank details info */}
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginTop:18 }}>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>💳 Step 2 — Register QuickServe as a Destination</div>
              <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.7 }}>
                In your Tap Dashboard, go to <strong>Marketplace → Destinations → Add Destination</strong> and enter
                QuickServe UAE's bank details. Once created, Tap gives you a <code>destination_id</code> —
                paste it in the "QuickServe Destination ID" field above.
                <br />
                <span style={{ color:'var(--text3)' }}>Contact <strong>support@quickserve.ae</strong> to get our bank details for your Tap account.</span>
              </div>
            </div>

            {/* Step 3: Vendor onboarding */}
            {mall?.tapMarketplaceKey && (vendors || []).length > 0 && (
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginTop:18 }}>
                <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>🏪 Step 3 — Onboard Your Vendors to Tap</div>
                <div style={{ fontSize:12, color:'var(--text3)', marginBottom:14 }}>
                  Each vendor needs a Tap sub-account so payments can be auto-split at checkout.
                  Vendors with ● Active status are auto-split. Others receive manual payout until onboarding completes.
                </div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr>
                      {['Vendor','Tap Status','Action'].map(h => (
                        <th key={h} style={{ textAlign:'left', padding:'8px 10px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(vendors || []).map(mv => {
                      const v = mv.vendor || mv;
                      const st = v.tapOnboardingStatus || 'NOT_STARTED';
                      const badge = st === 'ACTIVE' ? <span style={{ color:'var(--green)' }}>● Active</span>
                                  : st === 'LEAD_CREATED' ? <span style={{ color:'var(--accent)' }}>⏳ KYC Pending</span>
                                  : <span style={{ color:'var(--text3)' }}>○ Not Started</span>;
                      const btnLabel = st === 'ACTIVE' ? null
                                     : st === 'LEAD_CREATED' ? 'Check Status'
                                     : 'Start Onboarding';
                      return (
                        <tr key={v.id || mv.vendorId} style={{ borderBottom:'1px solid var(--border)' }}>
                          <td style={{ padding:'10px 10px', fontWeight:600 }}>{v.emoji || '🍽'} {v.name}</td>
                          <td style={{ padding:'10px 10px' }}>{badge}</td>
                          <td style={{ padding:'10px 10px' }}>
                            {btnLabel && (
                              <button className="btn btn-primary btn-sm" onClick={() => openOnboarding(mv)}>
                                {btnLabel}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Vendor Onboarding Modal ── */}
      {onboardVendor && (() => {
        const v = onboardVendor?.vendor || onboardVendor;
        const KYC_DOCS = [
          { key:'commercialReg', label:'Commercial Registration', required:true },
          { key:'tradeLicense',  label:'Trade License',          required:true },
          { key:'ownerId',       label:'Owner ID / Passport',    required:false },
          { key:'ibanCert',      label:'IBAN Certificate',       required:false },
        ];
        const allRequiredUploaded = KYC_DOCS.filter(d => d.required).every(d => uploadStatus[d.key] === 'done');
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
            <div style={{ background:'var(--bg2)', borderRadius:12, padding:24, width:'100%', maxWidth:520, maxHeight:'85vh', overflowY:'auto', border:'1px solid var(--border)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div style={{ fontWeight:700, fontSize:16 }}>Onboard: {v.name}</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setOnboardVendor(null)}>✕</button>
              </div>

              {/* Step indicator */}
              <div style={{ display:'flex', gap:8, marginBottom:20 }}>
                {['Business Details','KYC Documents','Review & Submit'].map((s, i) => (
                  <div key={i} style={{ flex:1, textAlign:'center', padding:'6px 4px', borderRadius:6, fontSize:12, fontWeight: onboardStep === i+1 ? 700 : 400,
                    background: onboardStep === i+1 ? 'var(--accent)' : 'var(--bg3)',
                    color: onboardStep === i+1 ? '#000' : 'var(--text3)' }}>
                    {i+1}. {s}
                  </div>
                ))}
              </div>

              {/* Step 1 — Business Details */}
              {onboardStep === 1 && (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {[
                    { key:'name',         label:'Business Name (EN)', placeholder:'Pizza Palace' },
                    { key:'nameAr',       label:'Business Name (AR)', placeholder:'بيتزا بالاس' },
                    { key:'email',        label:'Business Email',     placeholder:'finance@vendor.com' },
                    { key:'phone',        label:'Phone (without +971)',placeholder:'501234567' },
                  ].map(f => (
                    <div key={f.key}>
                      <div className="section-label" style={{ marginBottom:4 }}>{f.label}</div>
                      <input placeholder={f.placeholder} value={onboardForm[f.key] || ''}
                        onChange={e => setOnboardForm(p => ({ ...p, [f.key]: e.target.value }))} />
                    </div>
                  ))}
                  <div>
                    <div className="section-label" style={{ marginBottom:4 }}>Business Type</div>
                    <select value={onboardForm.businessType || 'corp'} onChange={e => setOnboardForm(p => ({ ...p, businessType: e.target.value }))}>
                      <option value="corp">Corporation</option>
                      <option value="individual">Individual</option>
                    </select>
                  </div>
                  <button className="btn btn-primary" onClick={() => setOnboardStep(2)}
                    disabled={!onboardForm.name || !onboardForm.email || !onboardForm.phone}>
                    Next →
                  </button>
                </div>
              )}

              {/* Step 2 — KYC Documents */}
              {onboardStep === 2 && (
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  <div style={{ fontSize:12, color:'var(--text3)', marginBottom:4 }}>
                    Upload documents using your mall's Tap marketplace key. Files are sent directly to Tap's secure storage.
                  </div>
                  {KYC_DOCS.map(doc => (
                    <div key={doc.key} style={{ padding:'12px 14px', background:'var(--bg3)', borderRadius:8, border:`1px solid ${uploadStatus[doc.key] === 'done' ? 'var(--green)' : 'var(--border)'}` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                        <div style={{ fontSize:13, fontWeight:600 }}>
                          {doc.label} {doc.required && <span style={{ color:'var(--red)', fontSize:11 }}>*</span>}
                        </div>
                        <div style={{ fontSize:12 }}>
                          {uploadStatus[doc.key] === 'uploading' && <span style={{ color:'var(--accent)' }}>⏳ Uploading…</span>}
                          {uploadStatus[doc.key] === 'done'      && <span style={{ color:'var(--green)' }}>✅ Uploaded</span>}
                          {uploadStatus[doc.key] === 'error'     && <span style={{ color:'var(--red)' }}>❌ Failed</span>}
                        </div>
                      </div>
                      <input type="file" accept="image/*,application/pdf"
                        onChange={e => { const f = e.target.files[0]; if (f) { setOnboardFiles(p => ({ ...p, [doc.key]: f })); uploadKycFile(doc.key, f); } }} />
                    </div>
                  ))}
                  <div style={{ display:'flex', gap:10 }}>
                    <button className="btn btn-ghost" onClick={() => setOnboardStep(1)}>← Back</button>
                    <button className="btn btn-primary" disabled={!allRequiredUploaded} onClick={() => setOnboardStep(3)}>Next →</button>
                  </div>
                </div>
              )}

              {/* Step 3 — Review & Submit */}
              {onboardStep === 3 && (
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  <div style={{ background:'var(--bg3)', borderRadius:8, padding:14 }}>
                    <div style={{ fontWeight:600, marginBottom:8 }}>Business Details</div>
                    {[['Name (EN)', onboardForm.name], ['Name (AR)', onboardForm.nameAr], ['Email', onboardForm.email], ['Phone', '+971 ' + onboardForm.phone], ['Type', onboardForm.businessType]].map(([l, v]) => v ? (
                      <div key={l} style={{ display:'flex', gap:10, fontSize:12, marginBottom:4 }}>
                        <div style={{ color:'var(--text3)', minWidth:80 }}>{l}</div>
                        <div>{v}</div>
                      </div>
                    ) : null)}
                  </div>
                  <div style={{ background:'var(--bg3)', borderRadius:8, padding:14 }}>
                    <div style={{ fontWeight:600, marginBottom:8 }}>Documents</div>
                    {KYC_DOCS.map(doc => (
                      <div key={doc.key} style={{ display:'flex', gap:10, fontSize:12, marginBottom:4 }}>
                        <div style={{ color:'var(--text3)', minWidth:140 }}>{doc.label}</div>
                        <div style={{ color: fileIds[doc.key] ? 'var(--green)' : 'var(--text3)' }}>
                          {fileIds[doc.key] ? '✅ ' + fileIds[doc.key] : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                  {onboardMsg && <div style={{ fontSize:13, color: onboardMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{onboardMsg}</div>}
                  <div style={{ display:'flex', gap:10 }}>
                    <button className="btn btn-ghost" onClick={() => setOnboardStep(2)} disabled={onboardSaving}>← Back</button>
                    <button className="btn btn-primary" onClick={submitOnboarding} disabled={onboardSaving}>
                      {onboardSaving ? '⏳ Submitting...' : 'Submit to Tap'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Food Court Service Fee ── */}
      <ServiceFeeSection mallId={mallId} mall={mall} onSaved={onSaved} />

      <button
        className="btn btn-primary"
        style={{ padding: '13px 32px', fontSize: 15 }}
        onClick={handleSave}
        disabled={saving}>
        {saving ? '⏳ Saving...' : '💾 Save All Settings'}
      </button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function MallAdminPanel() {
  const { mallAdmin, loginMall, logout: authLogout, role } = useAuth();
  const [tab,              setTab]              = useState('overview');
  const [mall,             setMall]             = useState(null);
  const [analytics,        setAnalytics]        = useState(null);
  const [settlement,       setSettlement]       = useState(null);
  const [pendingPayouts,      setPendingPayouts]      = useState([]);
  const [completedPayouts,    setCompletedPayouts]    = useState([]);
  const [completedPagination, setCompletedPagination] = useState(null);
  const [completedPage,       setCompletedPage]       = useState(1);
  const [completedDates,      setCompletedDates]      = useState(() => {
    const today   = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
    return { startDate: weekAgo, endDate: today };
  });
  const [completedVendorFilter, setCompletedVendorFilter] = useState('');
  const [heldDays,         setHeldDays]         = useState({}); // { [vendorId]: Set(dateStr) } — client-side only
  const [vendors,          setVendors]          = useState([]);
  const [allVendors,       setAllVendors]       = useState([]);
  const [paymentStats,     setPaymentStats]     = useState(null);
  const [loading,          setLoading]          = useState(false);
  const [settleDate,       setSettleDate]       = useState(new Date().toISOString().split('T')[0]);
  const [msg,              setMsg]              = useState('');
  const [onboarding,       setOnboarding]       = useState({}); // vendorId -> server checklist (UNDER_REVIEW only)
  const [suspendTarget,    setSuspendTarget]    = useState(null); // vendor being suspended
  const [suspendReason,    setSuspendReason]    = useState('');
  const [suspendError,     setSuspendError]     = useState('');
  const [suspendSaving,    setSuspendSaving]    = useState(false);
  const [auditTarget,      setAuditTarget]      = useState(null); // vendor whose audit log is open
  const [auditRows,        setAuditRows]        = useState(null); // null = loading
  const [inviteOpen,       setInviteOpen]       = useState(false);
  const [inviteForm,       setInviteForm]       = useState({ name: '', email: '', deploymentMode: 'MALL_MANAGED' });
  const [inviteSending,    setInviteSending]    = useState(false);
  const [resendTarget,     setResendTarget]     = useState(null); // vendor whose invite is being resent
  const [pricingTarget,    setPricingTarget]    = useState(null); // mallVendor.vendor being assigned pricing
  const [pricingModels,    setPricingModels]    = useState([]);
  const [pricingModelId,   setPricingModelId]   = useState('');
  const [pricingNotes,     setPricingNotes]     = useState('');
  const [pricingSaving,    setPricingSaving]    = useState(false);

 useEffect(() => {
    const mallId = mallAdmin?.mallId;
    const pending = vendors.filter(mv => mv.vendor?.status === 'UNDER_REVIEW');
    if (!mallId || !pending.length) return;
    Promise.allSettled(pending.map(mv => api.get(`/mall/${mallId}/vendors/${mv.vendor.id}/onboarding`)))
      .then(rs => {
        const next = {};
        rs.forEach((r, i) => { if (r.status === 'fulfilled') next[pending[i].vendor.id] = r.value.data; });
        setOnboarding(prev => ({ ...prev, ...next }));
      });
  }, [vendors, mallAdmin]);
  useEffect(() => { if (mallAdmin) loadData(); }, [mallAdmin, tab]);

  const showMsg = (text) => { setMsg(text); setTimeout(() => setMsg(''), 3000); };

  const loadData = async () => {
    const mallId = mallAdmin?.mallId;
    if (!mallId) return;
    setLoading(true);
    const [mallRes, analyticsRes, vendorsRes, allRes] = await Promise.allSettled([
      getMall(mallId),
      getMallAnalytics(mallId),
      api.get(`/mall/${mallId}/vendors`),
      api.get('/vendors'),
    ]);
    if (mallRes.status      === 'fulfilled') setMall(mallRes.value.data);
    if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
    else setAnalytics(null);
    if (vendorsRes.status   === 'fulfilled') setVendors(vendorsRes.value.data || []);
    if (allRes.status       === 'fulfilled') setAllVendors(allRes.value.data || []);
    setLoading(false);
  };

  const loadSettlement = async () => {
    setLoading(true);
    try { const r = await getMallSettlement(mallAdmin?.mallId, settleDate); setSettlement(r.data); }
    catch (_) { setSettlement(null); }
    finally { setLoading(false); }
  };

  const loadPayouts = async (page = completedPage, dates = completedDates, vendorFilter = completedVendorFilter) => {
    const mallId = mallAdmin?.mallId;
    if (!mallId) return;
    setLoading(true);
    const completedParams = {
      status: 'PAID_BY_MALL',
      page, limit: 50,
      startDate: dates.startDate,
      endDate:   dates.endDate,
      ...(vendorFilter && { vendorId: vendorFilter }),
    };
    const [pendingRes, completedRes] = await Promise.allSettled([
      api.get(`/mall/${mallId}/payouts`),
      api.get(`/mall/${mallId}/payouts`, { params: completedParams }),
    ]);
    setPendingPayouts(pendingRes.status === 'fulfilled' ? (pendingRes.value.data || []) : []);
    if (completedRes.status === 'fulfilled') {
      const d = completedRes.value.data;
      setCompletedPayouts(d.data || []);
      setCompletedPagination(d.pagination || null);
    } else {
      setCompletedPayouts([]);
      setCompletedPagination(null);
    }
    setLoading(false);
  };

  const loadPaymentStats = async () => {
    try { const r = await api.get(`/mall/${mallAdmin?.mallId}/payment-stats`); setPaymentStats(r.data); }
    catch (_) {}
  };

  const handleSettleDay = async (vendor, day) => {
    const ref = prompt(`Pay ${vendor.vendorName} for ${day.date} (AED ${day.net.toFixed(2)})?\nEnter transfer reference:`);
    if (!ref) return;
    const mallId = mallAdmin?.mallId;
    try {
      await api.post(`/mall/${mallId}/payouts/${vendor.vendorId}/mark-paid`, {
        transferRef: ref,
        date:        day.date,
      });
      await loadPayouts();
      showMsg('✅ Payout recorded');
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Failed'));
    }
  };

  const handleSettleAll = async (vendor, activeDays) => {
    const days = activeDays || vendor.days;
    if (!days.length) return;
    const total = parseFloat(days.reduce((s, d) => s + d.net, 0).toFixed(2));
    const ref = prompt(`Settle ${days.length} day(s) for ${vendor.vendorName}?\nAED ${total.toFixed(2)} · Enter transfer reference:`);
    if (!ref) return;
    const mallId = mallAdmin?.mallId;
    try {
      await api.post(`/mall/${mallId}/payouts/${vendor.vendorId}/mark-paid`, {
        transferRef: ref,
        settleAll:   true,
        dates:       days.map(d => d.date),
      });
      setHeldDays(prev => {
        const next = { ...prev };
        delete next[vendor.vendorId];
        return next;
      });
      await loadPayouts();
      showMsg('✅ All settled');
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Failed'));
    }
  };

  const toggleHold = (vendorId, date) => {
    setHeldDays(prev => {
      const set = new Set(prev[vendorId] || []);
      if (set.has(date)) set.delete(date); else set.add(date);
      return { ...prev, [vendorId]: set };
    });
  };

  const handleAddVendor = async (vendorId) => {
    try { await addVendorToMall(mallAdmin?.mallId, { vendorId }); showMsg('✅ Added'); loadData(); }
    catch (err) { showMsg('❌ ' + (err.response?.data?.error || 'Failed')); }
  };

  const handleInviteVendor = async () => {
    const email = inviteForm.email.trim().toLowerCase();
    if (!inviteForm.name.trim() || !email) return showMsg('❌ Name and email are required');
    setInviteSending(true);
    try {
      await api.post(`/mall/${mallAdmin?.mallId}/vendors/invite`, { ...inviteForm, name: inviteForm.name.trim(), email });
      showMsg(`✅ Invite sent to ${email}`);
      setInviteOpen(false);
      setInviteForm({ name: '', email: '', deploymentMode: 'MALL_MANAGED' });
      loadData();
    } catch (err) { showMsg('❌ ' + (err.response?.data?.error || 'Failed to send invite')); }
    finally { setInviteSending(false); }
  };

  const handleApproveVendor = async (vendor) => {
        try {
      await api.post(`/mall/${mallAdmin?.mallId}/vendors/${vendor.id}/approve`);
      showMsg(`✅ ${vendor.name} is now live`);
      loadData();
    } catch (err) {
      const e = err.response?.data;
      showMsg('❌ ' + (e?.missing?.length ? `${e.error}: ${e.missing.map(m => m.label).join(', ')}` : e?.error || 'Approve failed'));
    }
  };

  const openPricingModal = async (vendor) => {
    setPricingTarget(vendor); setPricingNotes('');
    setPricingModelId(vendor.vendorPricing?.[0]?.pricingModel?.id || '');
    try {
      const r = await api.get(`/mall/${mallAdmin?.mallId}/pricing-models`);
      setPricingModels(r.data || []);
    } catch (_) { setPricingModels([]); showMsg('❌ Could not load pricing plans'); }
  };

  const handleAssignPricing = async () => {
    if (!pricingModelId) return showMsg('❌ Select a pricing plan');
    setPricingSaving(true);
    try {
      const mallId = mallAdmin?.mallId;
      await api.post(`/mall/${mallId}/vendors/${pricingTarget.id}/pricing`, { pricingModelId, notes: pricingNotes });
      showMsg(`✅ Pricing assigned to ${pricingTarget.name}`);
      const r = await api.get(`/mall/${mallId}/vendors/${pricingTarget.id}/onboarding`);
      setOnboarding(prev => ({ ...prev, [pricingTarget.id]: r.data }));
      setPricingTarget(null); loadData();
    } catch (err) { showMsg('❌ ' + (err.response?.data?.error || 'Failed to assign pricing')); }
    finally { setPricingSaving(false); }
  };

  const openSuspend = (vendor) => { setSuspendTarget(vendor); setSuspendReason(''); setSuspendError(''); };
  const closeSuspend = () => { setSuspendTarget(null); setSuspendReason(''); setSuspendError(''); };

  // Errors are shown inside the modal — showMsg renders on the page behind the overlay.
  const handleSuspendVendor = async () => {
    const reason = suspendReason.trim();
    if (reason.length < 10) return setSuspendError(`Reason must be at least 10 characters (${reason.length}/10)`);
    setSuspendSaving(true); setSuspendError('');
    try {
      await api.post(`/mall/${mallAdmin?.mallId}/vendors/${suspendTarget.id}/suspend`, { reason });
      const name = suspendTarget.name;
      closeSuspend();
      showMsg(`✅ ${name} suspended`);
      loadData();
    } catch (err) { setSuspendError(err.response?.data?.error || err.message || 'Suspend failed'); }
    finally { setSuspendSaving(false); }
  };

  const handleReinstateVendor = async (vendor) => {
    try {
      await api.post(`/mall/${mallAdmin?.mallId}/vendors/${vendor.id}/unsuspend`);
      showMsg(`✅ ${vendor.name} reinstated`);
      loadData();
    } catch (err) { showMsg('❌ ' + (err.response?.data?.error || 'Reinstate failed')); }
  };

  const openAuditLog = async (vendor) => {
    setAuditTarget(vendor); setAuditRows(null);
    try {
      const r = await api.get(`/mall/${mallAdmin?.mallId}/vendors/${vendor.id}/audit-log?limit=50`);
      setAuditRows(r.data.data || []);
    } catch (_) { setAuditRows([]); showMsg('❌ Could not load audit log'); }
  };

  const handleResendInvite = async (vendor) => {
    setResendTarget(null);
    try {
      await api.post(`/mall/${mallAdmin?.mallId}/vendors/${vendor.id}/invite/resend`);
      showMsg('✅ Invite resent');
    } catch (err) { showMsg('❌ ' + (err.response?.data?.error || 'Failed to resend invite')); }
  };

  const handleToggleVendor = async (vendorId, active) => {
    try {
      if (active) await removeVendorFromMall(mallAdmin?.mallId, vendorId);
      else        await addVendorToMall(mallAdmin?.mallId, { vendorId });
      showMsg(active ? '✅ Deactivated' : '✅ Reactivated');
      loadData();
    } catch (_) { showMsg('❌ Failed'); }
  };

  if (!mallAdmin || role !== 'MALL_ADMIN') return <MallLogin onLogin={loginMall} />;

  const EMPTY_ANALYTICS = { totalSessions:0, todaySessions:0, vendors:0, totalRevenue:0, totalCommission:0 };
  const a = analytics || EMPTY_ANALYTICS;

  const tabStyle = (id) => ({
    padding:'8px 15px', borderRadius:8, fontSize:13, fontWeight:500,
    background: tab===id ? 'var(--accent)' : 'transparent',
    color:      tab===id ? '#000' : 'var(--text2)',
    border:'none', cursor:'pointer',
  });

  return (
    <div className="page">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <div className="page-title">🏬 Mall Admin Panel</div>
          <div className="page-sub">{mall?.name || 'Food Court'} · {mall?.billingMode || 'COMBINED'} billing · {mall?.commissionPct || 0}% commission</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={authLogout}>Logout</button>
      </div>

      {msg && (
        <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13, background:msg.startsWith('✅')?'#1a3d2a':'#3d1a1a', color:msg.startsWith('✅')?'var(--green)':'var(--red)' }}>
          {msg}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:3, marginBottom:24, background:'var(--bg2)', borderRadius:10, padding:4, width:'fit-content', flexWrap:'wrap' }}>
        {[['overview','📊 Overview'],['vendors','🍽 Vendors'],['pos','🔌 POS'],['payments','💳 Payment Health'],['access','🔑 Access'],['settlement','💰 Settlement'],['payouts','💰 Payouts'],['reports','📊 Reports'],['settings','⚙️ Settings'],['qr','📱 Table QR'],['operations','Operations']].map(([id,label]) => (
          <button key={id} style={tabStyle(id)} onClick={() => { setTab(id); if(id==='settlement')loadSettlement(); if(id==='payouts')loadPayouts(); if(id==='payments')loadPaymentStats(); }}>{label}</button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <>
          <div className="grid4" style={{ marginBottom:20 }}>
            {[['Total Sessions',a.totalSessions],["Today's",a.todaySessions],['Vendors',a.vendors],['Revenue',`AED ${Number(a.totalRevenue||0).toFixed(0)}`]].map(([l,v])=>(
              <div key={l} className="stat-card"><div className="stat-label">{l}</div><div className="stat-val">{v}</div></div>
            ))}
          </div>
          <div className="card" style={{ marginBottom:20 }}>
            <div className="section-label" style={{marginBottom:10}}>Menu Completeness</div>
            {(a.dietaryCoverage || []).length === 0 && <div style={{fontSize:13,color:'var(--text3)'}}>No vendors yet.</div>}
            {(a.dietaryCoverage || []).map(v => (
              <div key={v.vendorId} style={{display:'flex',justifyContent:'space-between',gap:12,fontSize:14,padding:'4px 0'}}>
                <span>{v.emoji} {v.name}</span>
                <span style={{color: v.percent >= 80 ? 'var(--green)' : v.percent === 0 ? 'var(--red)' : 'var(--text2)'}}>
                  {v.totalItems === 0 ? 'No menu items' : v.percent === 0 ? '0% — no dietary data ⚠' : `${v.percent}% items have dietary info${v.percent >= 80 ? ' ✅' : ''}`}
                </span>
              </div>
            ))}
            <div style={{fontSize:12,color:'var(--text3)',marginTop:8}}>
              Customers can filter menus by dietary preference — items without dietary tags are hidden from filtered views.
            </div>
          </div>
          <div className="grid2">
            <div className="card">
              <div className="section-label" style={{marginBottom:10}}>Commission Collected</div>
              <div style={{fontSize:30,fontWeight:700,color:'var(--green)'}}>AED {Number(a.totalCommission||0).toFixed(2)}</div>
              <div style={{fontSize:13,color:'var(--text2)',marginTop:4}}>{mall?.commissionPct||0}% of total revenue</div>
            </div>
            <div className="card">
              <div className="section-label" style={{marginBottom:10}}>Billing Mode</div>
              <div style={{fontSize:17,fontWeight:700,marginBottom:8}}>{mall?.billingMode==='COMBINED'?'🔗 Combined':'💳 Individual'}</div>
              <div style={{fontSize:13,color:'var(--text2)'}}>{mall?.billingMode==='COMBINED'?'Customer pays once, mall settles vendors':'Customer pays each vendor separately'}</div>
            </div>
          </div>
        </>
      )}

      {/* Vendors */}
      {tab === 'vendors' && (
        <>
          <div className="card" style={{padding:0,overflow:'hidden',marginBottom:16}}>
            <div style={{padding:'13px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,display:'flex',justifyContent:'space-between'}}>
              <span>Vendors ({vendors.length})</span>
              <span style={{display:'flex',alignItems:'center',gap:12}}>
                <span style={{fontSize:13,color:'var(--text2)',fontWeight:400}}>{vendors.filter(v=>v.isActive!==false).length} active</span>
                <button className="btn btn-primary btn-sm" onClick={()=>setInviteOpen(true)}>✉️ Invite Vendor</button>
              </span>
            </div>
            {inviteOpen && (
              <div style={{padding:16,borderBottom:'1px solid var(--border)',background:'var(--bg3)'}}>
                <div style={{fontWeight:700,marginBottom:12}}>Invite Vendor</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:10,marginBottom:12}}>
                  <input placeholder="Vendor name" value={inviteForm.name} onChange={e=>setInviteForm(f=>({...f,name:e.target.value}))} />
                  <input type="email" placeholder="Email" value={inviteForm.email} onChange={e=>setInviteForm(f=>({...f,email:e.target.value}))} />
                  <select value={inviteForm.deploymentMode} onChange={e=>setInviteForm(f=>({...f,deploymentMode:e.target.value}))}>
                    <option value="MALL_MANAGED">MALL_MANAGED — mall kiosk only</option>
                    <option value="MALL_PLUS_STALL">MALL_PLUS_STALL — mall kiosk + own stall</option>
                  </select>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-primary btn-sm" onClick={handleInviteVendor} disabled={inviteSending}>{inviteSending?'Sending...':'Send Invite'}</button>
                  <button className="btn btn-sm" onClick={()=>setInviteOpen(false)}>Cancel</button>
                </div>
              </div>
            )}
            {vendors.length===0 && <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>No vendors added</div>}
            {vendors.map((mv)=>{
              const MALL_COMPATIBLE = ['MALL_MANAGED', 'MALL_PLUS_STALL'];
              const incompatible = mv.vendor?.deploymentMode && !MALL_COMPATIBLE.includes(mv.vendor.deploymentMode);
              return (
              <div key={mv.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 16px',borderBottom:'1px solid var(--border)',opacity:mv.isActive===false?.6:1}}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <span style={{fontSize:26}}>{mv.vendor?.emoji||'🍽'}</span>
                  <div>
                    <div style={{fontWeight:600}}>{mv.vendor?.name}</div>
                    <div style={{fontSize:12,color:'var(--text2)'}}>{mv.stallNumber?`Stall ${mv.stallNumber}`:'No stall'} · {mv.vendor?.email}</div>
                    {mv.vendor?.status === 'SUSPENDED' && mv.vendor.suspendedReason && (
                      <div style={{fontSize:12,color:'var(--red)',marginTop:2}}>Suspended: {mv.vendor.suspendedReason}</div>
                    )}
                    {incompatible && (
                      <div style={{fontSize:11,color:'var(--accent)',marginTop:2}}>
                        ⚠ Mode: {mv.vendor.deploymentMode} — excluded from settlement. Change mode or remove.
                      </div>
                    )}
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  {(() => {
                    const st = mv.vendor?.status || 'ACTIVE';
                    const B = { INVITED:['🟡 Invited','badge-amber'], PENDING_KYC:['🔵 Pending KYC','badge-blue'], UNDER_REVIEW:['🔵 Under Review','badge-blue'], ACTIVE:['🟢 Active','badge-green'], SUSPENDED:['🔴 Suspended','badge-red'] };
                    const [label, cls] = B[st] || [st, 'badge-red'];
                    return <span className={`badge ${cls}`}>{label}{st==='ACTIVE'&&mv.isActive===false?' (inactive)':''}</span>;
                  })()}
                  {mv.vendor?.status === 'UNDER_REVIEW' && (() => {
                    const o = onboarding[mv.vendor.id];
                    const missing = o ? o.missing.map(m => m.label).join(', ') : 'Loading checklist…';
                    return (
                      <button className="btn btn-sm btn-primary" disabled={!o?.allComplete}
                        title={o?.allComplete ? 'Approve and go live' : `Incomplete: ${missing}`}
                        onClick={()=>handleApproveVendor(mv.vendor)}>
                        Approve{o ? ` (${o.completed}/${o.total})` : ''}
                      </button>
                    );
                  })()}
                  {mv.vendor?.status !== 'INVITED' && (
                    <button className="btn btn-sm btn-primary" onClick={()=>openPricingModal(mv.vendor)}
                      title={mv.vendor?.vendorPricing?.[0] ? `Current: ${mv.vendor.vendorPricing[0].pricingModel?.name}` : 'No pricing assigned'}>
                      {mv.vendor?.vendorPricing?.length ? 'Change Pricing' : 'Assign Pricing'}
                    </button>
                  )}
                  {['ACTIVE','UNDER_REVIEW'].includes(mv.vendor?.status || 'ACTIVE') && (
                    <button className="btn btn-sm btn-danger" onClick={()=>openSuspend(mv.vendor)}>Suspend</button>
                  )}
                  {mv.vendor?.status === 'SUSPENDED' && (
                    <button className="btn btn-sm btn-primary" onClick={()=>handleReinstateVendor(mv.vendor)}>Reinstate</button>
                  )}
                  <button className="btn btn-sm" style={{background:'none',border:'none',color:'var(--accent)',textDecoration:'underline',padding:0}}
                    onClick={()=>openAuditLog(mv.vendor)}>View Audit Log</button>
                  {mv.vendor?.status === 'INVITED' && (
                    <button className="btn btn-sm btn-primary" onClick={()=>setResendTarget(mv.vendor)}>Resend Invite</button>
                  )}
                  {(mv.vendor?.status || 'ACTIVE') === 'ACTIVE' && (
                    <button className={`btn btn-sm ${mv.isActive!==false?'btn-danger':'btn-primary'}`} onClick={()=>handleToggleVendor(mv.vendor?.id,mv.isActive!==false)}>
                      {mv.isActive!==false?'Deactivate':'Reactivate'}
                    </button>
                  )}
                </div>
              </div>
            );})}
          </div>
          {suspendTarget && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}>
              <div className="card" style={{width:420,maxWidth:'100%'}}>
                <div style={{fontWeight:700,marginBottom:6}}>Suspend {suspendTarget.name}</div>
                <div style={{fontSize:12,color:'var(--text2)',marginBottom:12}}>The vendor disappears from the kiosk immediately.</div>
                <div className="section-label" style={{marginBottom:6}}>Reason for suspension</div>
                <textarea rows={3} value={suspendReason} onChange={e=>{setSuspendReason(e.target.value);setSuspendError('');}} style={{width:'100%',boxSizing:'border-box',marginBottom:suspendError?6:14}} autoFocus />
                {suspendError && <div style={{fontSize:12,color:'var(--red)',marginBottom:12}}>❌ {suspendError}</div>}
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-danger btn-sm" onClick={handleSuspendVendor} disabled={suspendSaving}>{suspendSaving?'Suspending...':'Suspend Vendor'}</button>
                  <button className="btn btn-sm" onClick={closeSuspend} disabled={suspendSaving}>Cancel</button>
                </div>
              </div>
            </div>
          )}
          {resendTarget && (
            <ConfirmModal title="Resend invite" message={`Resend invite to ${resendTarget.email}? The old link will stop working.`}
              confirmLabel="Resend" danger={false} onConfirm={()=>handleResendInvite(resendTarget)} onCancel={()=>setResendTarget(null)} />
          )}
          {pricingTarget && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}>
              <div className="card" style={{width:460,maxWidth:'100%',maxHeight:'80vh',overflowY:'auto'}}>
                <div style={{fontWeight:700,marginBottom:6}}>{pricingTarget.vendorPricing?.length ? 'Change' : 'Assign'} Pricing — {pricingTarget.name}</div>
                <div style={{fontSize:12,color:'var(--text2)',marginBottom:12}}>
                  {pricingTarget.vendorPricing?.[0]
                    ? `Currently on ${pricingTarget.vendorPricing[0].pricingModel?.name}. The previous plan is deactivated.`
                    : 'This vendor has no pricing plan yet.'}
                </div>
                {pricingModels.length === 0 && <div style={{color:'var(--text3)',marginBottom:12}}>No active pricing plans available.</div>}
                {pricingModels.map(m => (
                  <label key={m.id} style={{display:'flex',gap:10,alignItems:'flex-start',padding:'10px 12px',marginBottom:8,cursor:'pointer',borderRadius:8,
                    background:'var(--bg3)',border:`1px solid ${pricingModelId===m.id?'var(--accent)':'var(--border)'}`}}>
                    <input type="radio" name="pricingModel" checked={pricingModelId===m.id} onChange={()=>setPricingModelId(m.id)} style={{width:'auto',marginTop:3}} />
                    <div>
                      <div style={{fontWeight:600}}>{m.name}{m.isDefault && <span className="badge badge-green" style={{marginLeft:8}}>Default</span>}</div>
                      <div style={{fontSize:12,color:'var(--text2)'}}>{m.type.replace(/_/g,' ')}{m.description ? ` · ${m.description}` : ''}</div>
                    </div>
                  </label>
                ))}
                <div className="section-label" style={{marginBottom:6,marginTop:12}}>Notes (optional)</div>
                <textarea rows={2} value={pricingNotes} onChange={e=>setPricingNotes(e.target.value)} style={{width:'100%',boxSizing:'border-box',marginBottom:14}} />
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-primary btn-sm" onClick={handleAssignPricing} disabled={pricingSaving||!pricingModelId}>{pricingSaving?'Saving...':'Confirm'}</button>
                  <button className="btn btn-sm" onClick={()=>setPricingTarget(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
          {auditTarget && (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}>
              <div className="card" style={{width:560,maxWidth:'100%',maxHeight:'80vh',overflowY:'auto'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                  <div style={{fontWeight:700}}>Audit Log — {auditTarget.name}</div>
                  <button className="btn btn-sm" onClick={()=>setAuditTarget(null)}>Close</button>
                </div>
                {auditRows === null && <div style={{color:'var(--text3)'}}>Loading...</div>}
                {auditRows && auditRows.length === 0 && <div style={{color:'var(--text3)'}}>No recorded actions yet.</div>}
                {(auditRows || []).map(e => (
                  <div key={e.id} style={{padding:'9px 0',borderBottom:'1px solid var(--border)'}}>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:13}}>
                      <strong>{e.action.replace(/_/g,' ')}</strong>
                      <span style={{color:'var(--text3)'}}>{new Date(e.createdAt).toLocaleString()}</span>
                    </div>
                    <div style={{fontSize:12,color:'var(--text2)'}}>
                      by {e.actorType === 'SYSTEM' ? 'System' : e.actorType === 'MALL_ADMIN' ? 'Mall admin' : e.actorType === 'SUPER_ADMIN' ? 'Platform admin' : e.actorType}
                      {e.meta?.reason ? ` · ${e.meta.reason}` : ''}
                      {e.meta?.daysLeft ? ` · ${e.meta.daysLeft} days left` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="card">
            <div style={{fontWeight:700,marginBottom:14}}>Add Vendor to Mall</div>
            {(() => {
              const MALL_COMPATIBLE = ['MALL_MANAGED', 'MALL_PLUS_STALL'];
              const available = allVendors.filter(v =>
                !vendors.find(mv => mv.vendor?.id === v.id) &&
                MALL_COMPATIBLE.includes(v.deploymentMode)
              );
              if (available.length === 0)
                return <div style={{color:'var(--text3)',fontSize:13}}>No mall-compatible vendors available to add (vendors must be in MALL_MANAGED or MALL_PLUS_STALL mode)</div>;
              return available.map(v=>(
                  <div key={v.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 14px',background:'var(--bg3)',borderRadius:8,marginBottom:8}}>
                    <span style={{fontWeight:600}}>{v.emoji||'🍽'} {v.name} <span style={{fontSize:11,color:'var(--text3)'}}>({v.deploymentMode})</span></span>
                    <button className="btn btn-primary btn-sm" onClick={()=>handleAddVendor(v.id)}>+ Add</button>
                  </div>
                ));
            })()}
          </div>
        </>
      )}

      {/* POS Credentials */}
      {tab === 'pos' && (
        <VendorPOSTab
          mallId={mallAdmin?.mallId}
          vendors={vendors}
        />
      )}

      {/* Access */}
      {tab === 'access' && (
        <div className="card">
          <div style={{fontWeight:700,fontSize:15,marginBottom:16}}>🔑 Vendor Login Guide</div>
          {[
            ['1. Invite','Mall Admin → Vendors tab → ✉️ Invite Vendor. They get an email link valid for 72 hours'],
            ['2. Vendor activates','They open the link and set a password; the account moves to Pending KYC'],
            ['3. Vendor logs in','Open http://localhost:3000 → any tab → login with their credentials'],
            ['4. Vendor manages menu','Vendor Admin tab: add items, set prices, manage categories, toggle payment methods'],
            ['5. Orders appear','Customer orders via 🏬 Multi-Vendor — orders route to each vendor\'s kitchen'],
          ].map(([title,desc])=>(
            <div key={title} style={{padding:'12px 14px',background:'var(--bg3)',borderRadius:8,border:'1px solid var(--border)',marginBottom:8}}>
              <div style={{fontWeight:600,marginBottom:4}}>{title}</div>
              <div style={{fontSize:13,color:'var(--text2)'}}>{desc}</div>
            </div>
          ))}
          <div style={{marginTop:16}}>
            <div style={{fontWeight:700,marginBottom:12}}>Current Vendors</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr>{['Name','Email','Status'].map(h=><th key={h} style={{textAlign:'left',padding:'8px 12px',borderBottom:'1px solid var(--border)',fontSize:11,color:'var(--text3)',textTransform:'uppercase'}}>{h}</th>)}</tr></thead>
              <tbody>
                {vendors.map(mv=>(
                  <tr key={mv.id}>
                    <td style={{padding:'10px 12px',fontWeight:600}}>{mv.vendor?.emoji} {mv.vendor?.name}</td>
                    <td style={{padding:'10px 12px',color:'var(--text2)',fontSize:12,fontFamily:'monospace'}}>{mv.vendor?.email}</td>
                    <td style={{padding:'10px 12px'}}><span className={`badge ${mv.isActive!==false?'badge-green':'badge-red'}`}>{mv.isActive!==false?'Active':'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Settlement */}
      {tab === 'settlement' && (
        <>
          <div style={{display:'flex',gap:12,marginBottom:20,alignItems:'center',flexWrap:'wrap'}}>
            <input type="date" value={settleDate} onChange={e=>setSettleDate(e.target.value)} style={{width:180}}/>
            <button className="btn btn-primary" onClick={loadSettlement} disabled={loading}>{loading?'Loading...':'View Settlement'}</button>
          </div>
          {settlement && (() => {
            // Support both legacy shape (totalRevenue/vendors[].gross) and new shape (totals/vendors[].grossSales)
            const t = settlement.totals || {};
            const totalOrders   = t.totalOrders   ?? settlement.totalSessions   ?? 0;
            const totalGross    = t.totalGrossSales?? settlement.totalRevenue    ?? 0;
            const totalTax      = t.totalTax       ?? 0;
            const totalComm     = t.totalCommission?? settlement.totalCommission ?? 0;
            const totalNet      = t.totalNetPayable?? (totalGross - totalComm);
            const sf            = settlement.serviceFees || {};
            return (
              <>
                <div className="grid4" style={{marginBottom:16}}>
                  {[['Orders',totalOrders],['Gross Revenue',`AED ${Number(totalGross).toFixed(2)}`],['Commission',`AED ${Number(totalComm).toFixed(2)}`],['Net Payable',`AED ${Number(totalNet).toFixed(2)}`]].map(([l,v])=>(
                    <div key={l} className="stat-card"><div className="stat-label">{l}</div><div className="stat-val" style={{fontSize:16}}>{v}</div></div>
                  ))}
                </div>
                {sf.total > 0 && (
                  <div className="card" style={{marginBottom:16,padding:'12px 16px',display:'flex',gap:24,flexWrap:'wrap'}}>
                    <div><div style={{fontSize:11,color:'var(--text3)',textTransform:'uppercase',marginBottom:4}}>Service Fees Collected</div><div style={{fontWeight:700,color:'var(--accent)'}}>AED {Number(sf.total).toFixed(2)}</div></div>
                    <div><div style={{fontSize:11,color:'var(--text3)',textTransform:'uppercase',marginBottom:4}}>Platform Share</div><div style={{fontWeight:700}}>AED {Number(sf.platformShare||0).toFixed(2)}</div></div>
                    <div><div style={{fontSize:11,color:'var(--text3)',textTransform:'uppercase',marginBottom:4}}>Mall Share</div><div style={{fontWeight:700}}>AED {Number(sf.mallShare||0).toFixed(2)}</div></div>
                  </div>
                )}
                <div className="card" style={{padding:0,overflow:'hidden'}}>
                  <div style={{padding:'13px 16px',borderBottom:'1px solid var(--border)',fontWeight:700}}>Per-Vendor Breakdown ({(settlement.vendors||[]).length} vendors)</div>
                  {(settlement.vendors||[]).length === 0
                    ? <div style={{textAlign:'center',padding:32,color:'var(--text3)'}}>No orders on this date</div>
                    : <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                        <thead><tr>{['Vendor','Orders','Gross Sales','Tax','Commission','Net Payable','Settlement'].map(h=><th key={h} style={{textAlign:'left',padding:'10px 14px',borderBottom:'1px solid var(--border)',fontSize:11,color:'var(--text3)',textTransform:'uppercase'}}>{h}</th>)}</tr></thead>
                        <tbody>
                          {(settlement.vendors||[]).map(v=>{
                            const sm = v.settlementMethod;
                            const settlLabel = sm === 'AUTO_TAP'
                              ? <span style={{color:'var(--green)',fontSize:12}}>🔄 Auto-split via Tap</span>
                              : sm === 'MANUAL_PENDING'
                              ? <span style={{color:'var(--accent)',fontSize:12}}>📋 Manual pending</span>
                              : <span style={{color:'var(--text3)',fontSize:12}}>📋 Manual settlement</span>;
                            return (
                            <tr key={v.vendorId} style={{borderBottom:'1px solid var(--border)'}}>
                              <td style={{padding:'11px 14px',fontWeight:600}}>{v.emoji||'🍽'} {v.vendorName||v.name}</td>
                              <td style={{padding:'11px 14px',color:'var(--text2)'}}>{v.orderCount??v.orders}</td>
                              <td style={{padding:'11px 14px'}}>AED {Number(v.grossSales??v.gross??0).toFixed(2)}</td>
                              <td style={{padding:'11px 14px',color:'var(--text2)'}}>AED {Number(v.taxCollected??0).toFixed(2)}</td>
                              <td style={{padding:'11px 14px',color:'var(--red)'}}>- AED {Number(v.commissionAmount??v.commission??0).toFixed(2)}</td>
                              <td style={{padding:'11px 14px',fontWeight:700,color:'var(--green)'}}>AED {Number(v.netPayable??v.net??0).toFixed(2)}</td>
                              <td style={{padding:'11px 14px'}}>{settlLabel}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                  }
                </div>
              </>
            );
          })()}
        </>
      )}

      {/* Payment Health */}
      {tab === 'operations' && (
        <div style={{ padding:'4px 0' }}>
          <MallOperationsTab mallId={mallAdmin?.mallId} />
        </div>
      )}
      {tab === 'payments' && (
        <MallPaymentHealth stats={paymentStats} onRefresh={loadPaymentStats} />
      )}

      {/* Payouts */}
      {tab === 'payouts' && (() => {
        const mode = mall?.collectionMode;
        if (mode === 'MALL_COLLECTOR' || mode === 'MALL_MARKETPLACE') {
          return (
            <>
              {mode === 'MALL_MARKETPLACE' && pendingPayouts.length > 0 && (
                <div style={{padding:'10px 14px',background:'#1a2d1a',borderRadius:8,marginBottom:16,fontSize:13,border:'1px solid var(--green)'}}>
                  🔄 This mall uses Tap Marketplace. Vendors with a Tap Retailer ID are auto-settled at checkout and do not appear below.
                  Only vendors without a Retailer ID and cash orders require manual payout.
                </div>
              )}
              {mode === 'MALL_MARKETPLACE' && pendingPayouts.length === 0 && (
                <div style={{padding:'10px 14px',background:'var(--bg3)',borderRadius:8,marginBottom:16,fontSize:13,color:'var(--text2)',border:'1px solid var(--border)'}}>
                  ✅ All vendors are onboarded on Tap Marketplace. No manual payouts pending.
                </div>
              )}
              <PayoutsTab
                pendingPayouts={pendingPayouts}
                completedPayouts={completedPayouts}
                onSettleDay={handleSettleDay}
                onSettleAll={handleSettleAll}
                heldDays={heldDays}
                onToggleHold={toggleHold}
                completedDates={completedDates}
                setCompletedDates={setCompletedDates}
                completedPage={completedPage}
                setCompletedPage={setCompletedPage}
                completedVendorFilter={completedVendorFilter}
                setCompletedVendorFilter={setCompletedVendorFilter}
                completedPagination={completedPagination}
                loadPayouts={loadPayouts}
                vendors={vendors}
              />
            </>
          );
        }
        if (!mode || mode === 'PLATFORM_DEFAULT') {
          return (
            <div className="card" style={{maxWidth:620}}>
              <div style={{fontSize:22,marginBottom:12}}>💳</div>
              <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>Payments collected by platform</div>
              <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.6,marginBottom:16}}>
                Payments are collected by the QuickServe platform. Vendor settlements are handled
                automatically — no action required from your team.
              </div>
              <div style={{padding:'10px 14px',background:'var(--bg3)',borderRadius:8,fontSize:13,color:'var(--text3)',border:'1px solid var(--border)'}}>
                To enable direct payouts (where your team pays vendors), contact QuickServe support to switch
                your collection mode to <strong>Mall Collector</strong>.
              </div>
            </div>
          );
        }
        return (
          <div className="card" style={{maxWidth:540,color:'var(--text3)',fontSize:13}}>
            Collection mode not configured. Contact QuickServe support.
          </div>
        );
      })()}

      {tab === 'qr' && <MallTablesTab mall={mall} />}
      {tab === 'reports' && (
        <MallReportsTab mallId={mallAdmin?.mallId} vendors={vendors} />
      )}
      {tab === 'settings' && (
        <SettingsTab
          mall={mall}
          mallId={mallAdmin?.mallId}
          onSaved={loadData}
          vendors={vendors}
        />
      )}
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────
function MallLogin({ onLogin }) {
  const [email,   setEmail]   = useState('admin@mall.com');
  const [pass,    setPass]    = useState('admin123');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    setLoading(true); setError('');
    try {
      // onLogin is loginMall from AuthContext — sets user/role state on success
      await onLogin(email, pass);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg)'}}>
      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:40,width:380}}>
        <div style={{textAlign:'center',marginBottom:32}}>
          <div style={{fontSize:32}}>🏬</div>
          <div style={{fontSize:20,fontWeight:700,color:'var(--accent)',marginTop:8}}>Mall Admin Login</div>
        </div>
        <div style={{marginBottom:14}}><div className="section-label" style={{marginBottom:6}}>Email</div><input type="email" value={email} onChange={e=>setEmail(e.target.value)}/></div>
        <div style={{marginBottom:24}}><div className="section-label" style={{marginBottom:6}}>Password</div><input type="password" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handle()}/></div>
        {error && <div style={{color:'var(--red)',fontSize:13,marginBottom:12,textAlign:'center'}}>{error}</div>}
        <button className="btn btn-primary" style={{width:'100%',padding:13}} onClick={handle} disabled={loading}>{loading?'Logging in...':'Sign In'}</button>
        <div style={{marginTop:14,textAlign:'center'}}>
          <a href="/forgot-password?type=malladmin"
             style={{fontSize:13,color:'var(--text2)',textDecoration:'none'}}
             onMouseOver={e=>e.target.style.color='var(--accent)'}
             onMouseOut={e=>e.target.style.color='var(--text2)'}>
            Forgot password?
          </a>
        </div>
        <div style={{marginTop:10,fontSize:12,color:'var(--text3)',textAlign:'center'}}>Demo: admin@mall.com / admin123</div>
      </div>
    </div>
  );
}


